import './styles.css'

import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from 'react'

import Alert from './Alert'
import TutorialOverlay from './TutorialOverlay'
import { primaryDictionaryArray, secondaryDictionary, version, wordPairIndices, transitionCounts } from './data'
import * as storage from './storage/gameStorage'
import { createPathFinder, getLetterDiffIndex } from './utils/pathFinding'
import useUpdateChecker from './hooks/useUpdateChecker'

// Create Set for fast lookups
const primaryDictionary = new Set(primaryDictionaryArray)

// Create path finder with the game's dictionaries
const pathFinder = createPathFinder(primaryDictionary, secondaryDictionary)

// Export functions from path finder for use in this module
const {
  isValidWord,
  findShortestPathLength,
  findShortestPathLengthAllWords,
  findShortestPath,
  findShortestPathAllWords,
  findShortestPathExcluding,
  findShortestPathAllWordsExcluding,
  findDivergencePoint
} = pathFinder

// Check if a path contains any secondary dictionary words
const pathContainsSecondaryWord = (path) => path.some(word => secondaryDictionary.has(word))

// Fixed word pair index for tutorial mode - ensures consistent demo experience
const TUTORIAL_WORD_INDEX = 42

// Animation timing constant
const FLY_DURATION = 400

// Helper functions for index-based word pairs
const getWordPair = (level, index) => {
  const [startIdx, endIdx] = wordPairIndices[level - 1][index]
  return [primaryDictionaryArray[startIdx], primaryDictionaryArray[endIdx]]
}

// Cache for computed optimal paths
const optimalPathCache = new Map()

const getOptimalPath = (level, index) => {
  const cacheKey = `${level}-${index}`
  if (optimalPathCache.has(cacheKey)) {
    return optimalPathCache.get(cacheKey)
  }
  const [startWord, endWord] = getWordPair(level, index)
  const path = findShortestPath(startWord, endWord)
  optimalPathCache.set(cacheKey, path)
  return path
}

const getOptimalLength = (level) => transitionCounts[level - 1]

// Varied messages for when all levels are solved (but not all optimally)
const allLevelsSolvedMessages = [
  'magnificent!\nall levels solved!',
  'well done!\nall levels solved!',
  'fantastic!\nall levels solved!',
  'brilliant!\nall levels solved!',
  'impressive!\nall levels solved!',
  'excellent!\nall levels solved!',
  'wonderful!\nall levels solved!',
  'splendid!\nall levels solved!',
]

// Helper to check if a message is an "all levels solved" celebration message
const isAllLevelsSolvedMessage = (msg) => msg && msg.includes('all levels solved')

// For compatibility: number of word pairs per level
const wordPairsLength = wordPairIndices[0].length

const calculateIndexByDate = () => {
  const date = new Date()

  // Shift the time *back* by 3 hours
  const shifted = new Date(date.getTime() - 3 * 60 * 60 * 1000)

  const localMilliseconds = new Date(
    shifted.getFullYear(),
    shifted.getMonth(),
    shifted.getDate()
  ).getTime()

  const daysSinceEpoch = localMilliseconds / (24 * 60 * 60 * 1000)
  return Math.floor(daysSinceEpoch)
}

const LAUNCH_DAYS_SINCE_EPOCH = Math.floor(new Date(2025, 0, 30).getTime() / (24 * 60 * 60 * 1000))

// Feature announcements — newest first. Prepend a new entry to announce future features.
const ANNOUNCEMENTS = [
  {
    id: 'play-past-levels',
    text: 'You can now play past puzzles!',
  },
]

// Setup click-and-drag horizontal scrolling for desktop
const setupDragScroll = (element) => {
  if (!element) return () => {}

  let isDragging = false
  let hasMoved = false
  let startX = 0
  let scrollLeftStart = 0

  const onMouseDown = (e) => {
    isDragging = true
    hasMoved = false
    startX = e.pageX
    scrollLeftStart = element.scrollLeft
    element.style.cursor = 'grabbing'
    e.preventDefault()
  }

  const onMouseMove = (e) => {
    if (!isDragging) return
    e.preventDefault()
    const walk = (e.pageX - startX) * 1.5
    if (Math.abs(walk) > 3) hasMoved = true
    element.scrollLeft = scrollLeftStart - walk
  }

  const onMouseUp = () => {
    if (!isDragging) return
    isDragging = false
    element.style.cursor = 'grab'
    // Clear any accidental text selection that occurred during drag
    if (hasMoved) {
      window.getSelection()?.removeAllRanges()
      // Reset hasMoved after a brief delay so click handler can see it
      setTimeout(() => { hasMoved = false }, 0)
    }
  }

  // Prevent click from selecting text after drag
  const onClick = (e) => {
    if (hasMoved) {
      e.preventDefault()
      e.stopPropagation()
      window.getSelection()?.removeAllRanges()
    }
  }

  element.style.cursor = 'grab'
  element.addEventListener('mousedown', onMouseDown)
  element.addEventListener('click', onClick, true)
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)

  return () => {
    element.removeEventListener('mousedown', onMouseDown)
    element.removeEventListener('click', onClick, true)
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }
}

const QwertyKeyboard = ({
  handleKeyDown,
  handleShare,
  handleHelpClick,
  cursorPosition,
  onKeyboardTapWithoutSelection,
}) => {
  const isDisabled = cursorPosition === -1

  const handleClick = (key) => {
    if (isDisabled) {
      onKeyboardTapWithoutSelection()
      return
    }
    handleKeyDown({ key })
  }

  return (
    <div className="keyboard-container">
      {/* First row with 10 keys */}
      <div className="keyboard-row">
        {['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'].map((key) => (
          <button
            key={key}
            data-key={key.toLowerCase()}
            onClick={() => handleClick(key)}
            className="keyboard-key"
            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
          >
            {key}
          </button>
        ))}
      </div>
      {/* Second row with 9 keys */}
      <div className="keyboard-row">
        {['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'].map((key) => (
          <button
            key={key}
            data-key={key.toLowerCase()}
            onClick={() => handleClick(key)}
            className="keyboard-key"
            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
          >
            {key}
          </button>
        ))}
      </div>
      {/* Third row with 7 keys */}
      <div className="keyboard-row" style={{ justifyContent: 'center' }}>
        {['z', 'x', 'c', 'v', 'b', 'n', 'm'].map((key) => (
          <button
            key={key}
            data-key={key.toLowerCase()}
            onClick={() => handleClick(key)}
            className="keyboard-key"
            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
          >
            {key}
          </button>
        ))}
      </div>


    </div>
  )
}

const ScoreCircles = ({
  score,
  best,
  words,
  currentLevel,
  handleWordClick,
  expandedCircle,
  handleScoreCircleClick,
  handleBestCircleClick,
  hintedIndices = new Map(),
  boostedIndices = new Set(),
  bestHintedIndices = new Map(),
  bestBoostedIndices = new Set(),
  hintIndex = -1,
  optimalLength = 0,
  hasFirstTransformation = false,
  achievedNewBest = false,
  isCurrentLevelSolvedOptimally = false,
}) => {
  const scrollRef = useRef(null)
  const bestScrollRef = useRef(null)

  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
    }
    if (bestScrollRef.current && expandedCircle === 'best') {
      bestScrollRef.current.scrollLeft = bestScrollRef.current.scrollWidth
    }
  }, [words, expandedCircle, best])

  // Setup drag scrolling for desktop
  useEffect(() => {
    const cleanup1 = setupDragScroll(scrollRef.current)
    const cleanup2 = setupDragScroll(bestScrollRef.current)
    return () => {
      cleanup1()
      cleanup2()
    }
  }, [])

  const isZero = (score === 0 && !hasFirstTransformation) || achievedNewBest
  const isScoreCollapsed = isZero && expandedCircle !== 'score'
  // Hide the score circle completely when it would be collapsed/invisible
  // This prevents the invisible element from pushing the best circle to the right
  const hideScoreCircle = isScoreCollapsed || (expandedCircle === 'best' && score === 0)

  // When only best circle is visible (hideScoreCircle=true), left-justify it
  // When both circles visible, use space-between
  const hasBothCircles = !hideScoreCircle && best < 999

  return (
    <div
      style={{
        textAlign: 'center',
        margin: '1em 0 0.5em 0',
        fontSize: '1.25em',
        fontWeight: 'bold',
        color: `var(--level-${currentLevel}-text-color)`,
        display: 'flex',
        justifyContent: hasBothCircles ? 'space-between' : 'flex-start',
        alignItems: 'center',
        width: '100%',
        minHeight: '2em',
      }}
    >
      {!hideScoreCircle && (
      <div
        onClick={handleScoreCircleClick}
        style={{
          borderRadius: '9999px',
          backgroundColor: `var(--score-circle-color)`,
          cursor: isScoreCollapsed ? 'auto' : 'pointer',
          minWidth: isScoreCollapsed ? '0' : '2em',
          width: isScoreCollapsed ? '0' : (expandedCircle === 'score' ? 'auto' : '2em'),
          maxWidth: isScoreCollapsed ? '0' : (expandedCircle === 'score' ? 'none' : '2em'),
          opacity: isScoreCollapsed ? '0' : 'inherit',
          height: '2em',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: 'width 0.3s ease, max-width 0.3s ease, min-width 0.3s ease, opacity 0.3s ease',
          flexShrink: 1,
          position: 'relative',
        }}
      >
        {/* Collapsed state - always rendered */}
        <div
          style={{
            display: expandedCircle === 'score' ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            transition: 'opacity 0.3s ease',
          }}
        >
          {score}
        </div>

        {/* Expanded state - always rendered */}
          <div
            ref={scrollRef}
            style={{
            display: expandedCircle === 'score' ? 'inline-flex' : 'none',
              alignItems: 'center',
            justifyContent: 'flex-start',
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              height: '100%',
            padding: '0.25em 0.75em',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            transition: 'opacity 0.3s ease',
            }}
            onClick={(e) => {
              // prevent parent pill click handler from overwriting modalWords
              e.stopPropagation()
            }}
          >
            {words.map((word, index) => {
                  // index 0 is start word (pathIndex -1), subsequent words have pathIndex = index - 1
                  const pathIndex = index - 1
                  const hintedLetterIdx = hintedIndices.get(pathIndex)
                  const isBoosted = pathIndex >= 0 && boostedIndices.has(pathIndex)
                  // Check if this is the current word (last in path) and has an active LETTER hint
                  const isCurrentWordWithHint = index === words.length - 1 && hintIndex >= 0

                  // Render the word - if hinted, highlight just the hinted letter in blue; if boosted, highlight whole word in purple
                  // If both boosted AND hinted: show word in purple except the hinted letter which stays blue
                  const renderWord = () => {
                    if (isBoosted && hintedLetterIdx !== undefined) {
                      // Boosted word with a hinted letter - purple for boosted letters, blue for hinted letter
                      return word.split('').map((letter, letterIdx) => (
                        <span key={letterIdx} style={
                          letterIdx === hintedLetterIdx
                            ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                            : { color: 'var(--uchu-purple)', fontWeight: 'bold' }
                        }>{letter}</span>
                      ))
                    } else if (isBoosted && isCurrentWordWithHint) {
                      // Boosted current word with active LETTER hint - purple for boosted letters, blue for hinted letter
                      return word.split('').map((letter, letterIdx) => (
                        <span key={letterIdx} style={
                          letterIdx === hintIndex
                            ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                            : { color: 'var(--uchu-purple)', fontWeight: 'bold' }
                        }>{letter}</span>
                      ))
                    } else if (hintedLetterIdx !== undefined) {
                      // Hinted word only - render each letter, with hinted letter in blue
                      return word.split('').map((letter, letterIdx) => (
                        <span key={letterIdx} style={
                          letterIdx === hintedLetterIdx
                            ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                            : {}
                        }>{letter}</span>
                      ))
                    } else if (isCurrentWordWithHint) {
                      // Current word with active LETTER hint - highlight the hinted letter
                      return word.split('').map((letter, letterIdx) => (
                        <span key={letterIdx} style={
                          letterIdx === hintIndex
                            ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                            : {}
                        }>{letter}</span>
                      ))
                    } else if (isBoosted) {
                      // Boosted word only - render whole word in purple
                      return <span style={{ color: 'var(--uchu-purple)', fontWeight: 'bold' }}>{word}</span>
                    }
                    return word
                  }

                  return (
                  <span
                    key={index}
                    data-path-word={index}
                    onClick={(e) => { e.stopPropagation(); handleWordClick(index, false) }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ margin: '0 3px' }}>{renderWord()}</span>
                    {index < words.length - 1 && (
                      <span
                        style={{
                          display: 'inline-flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          margin: '0 3px',
                          flexShrink: 0,
                          pointerEvents: 'none',
                        }}
                      >
                        <span
                          style={{ fontSize: '0.6em', marginBottom: '-8px' }}
                        >
                          {index + 1}
                        </span>
                        <span
                          style={{ margin: '-5px 0', paddingBottom: '10px' }}
                        >
                          →
                        </span>
                      </span>
                    )}
                  </span>
                  )
                })}
          </div>
        {/* Fade overlays for scroll indication */}
        {expandedCircle === 'score' && (
          <>
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '1.25em',
              background: 'linear-gradient(to right, var(--score-circle-color), transparent)',
              borderRadius: '9999px 0 0 9999px',
              pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '1.25em',
              background: 'linear-gradient(to left, var(--score-circle-color), transparent)',
              borderRadius: '0 9999px 9999px 0',
              pointerEvents: 'none',
            }} />
          </>
        )}
      </div>
      )}

      {/* Show optimal path hint when no current path and no best exists */}
      {hideScoreCircle && best >= 999 && optimalLength > 0 && (
        <div
          style={{
            color: 'var(--uchu-dark-gray)',
            fontWeight: 'bold',
            width: '100%',
            textAlign: 'center',
          }}
        >
          optimal path in {optimalLength} steps
        </div>
      )}

      {best < 999 && (
        <div
          data-best-circle
          onClick={handleBestCircleClick}
          style={{
            borderRadius: '9999px',
            backgroundColor: isCurrentLevelSolvedOptimally ? 'var(--target-color)' : 'var(--best-circle-color)',
            cursor: 'pointer',
            minWidth: '2em',
            width: expandedCircle === 'best' ? 'auto' : '2em',
            maxWidth: expandedCircle === 'best' ? 'none' : '2em',
            height: '2em',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'width 0.3s ease, max-width 0.3s ease, min-width 0.3s ease, opacity 0.3s ease',
            marginLeft: '0em',
            flexShrink: 1,
            position: 'relative',
          }}
        >
          {/* Collapsed state - always rendered */}
            <div
              style={{
              display: expandedCircle === 'best' ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              transition: 'opacity 0.3s ease',
            }}
          >
            {best}
          </div>

          {/* Expanded state - always rendered */}
          <div
            ref={bestScrollRef}
            style={{
              display: expandedCircle === 'best' ? 'inline-flex' : 'none',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
                height: '100%',
                padding: '0.25em 0.75em',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              transition: 'opacity 0.3s ease',
              }}
              onClick={(e) => {
                // prevent scroll reset
                e.stopPropagation()
              }}
            >
              {/* Best path - with hint/boost highlighting */}
              {words.map((word, index) => {
                  const pathIndex = index - 1
                  const hintedLetterIdx = bestHintedIndices.get(pathIndex)
                  const isBoosted = pathIndex >= 0 && bestBoostedIndices.has(pathIndex)

                  // Render the word - if hinted, highlight just the hinted letter in blue; if boosted, highlight whole word in purple
                  // If both boosted AND hinted: show word in purple except the hinted letter which stays blue
                  const renderWord = () => {
                    if (isBoosted && hintedLetterIdx !== undefined) {
                      // Boosted word with a hinted letter - purple for boosted letters, blue for hinted letter
                      return word.split('').map((letter, letterIdx) => (
                        <span key={letterIdx} style={
                          letterIdx === hintedLetterIdx
                            ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                            : { color: 'var(--uchu-purple)', fontWeight: 'bold' }
                        }>{letter}</span>
                      ))
                    } else if (hintedLetterIdx !== undefined) {
                      // Hinted word only - render each letter, with hinted letter in blue
                      return word.split('').map((letter, letterIdx) => (
                        <span key={letterIdx} style={
                          letterIdx === hintedLetterIdx
                            ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                            : {}
                        }>{letter}</span>
                      ))
                    } else if (isBoosted) {
                      // Boosted word only - render whole word in purple
                      return <span style={{ color: 'var(--uchu-purple)', fontWeight: 'bold' }}>{word}</span>
                    }
                    return word
                  }

                  return (
                <span
                  key={index}
                  data-best-path-word={index}
                  onClick={() => {
                    if (index === words.length - 1) {
                      return
                    }

                    handleWordClick(index, true)
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    userSelect: 'none',
                  }}
                >
                  <span style={{ margin: '0 3px' }}>{renderWord()}</span>
                  {index < words.length - 1 && (
                    <span
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: '0 3px',
                        flexShrink: 0,
                        pointerEvents: 'none',
                      }}
                    >
                      <span
                        style={{ fontSize: '0.6em', marginBottom: '-8px' }}
                      >
                        {index + 1}
                      </span>
                      <span
                        style={{ margin: '-5px 0', paddingBottom: '10px' }}
                      >
                        →
                      </span>
                    </span>
                  )}
                </span>
                  )
              })}
            </div>
        {/* Fade overlays for scroll indication */}
        {expandedCircle === 'best' && (
          <>
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '1em',
              background: `linear-gradient(to right, ${isCurrentLevelSolvedOptimally ? 'var(--target-color)' : 'var(--best-circle-color)'}, transparent)`,
              borderRadius: '9999px 0 0 9999px',
              pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '1em',
              background: `linear-gradient(to left, ${isCurrentLevelSolvedOptimally ? 'var(--target-color)' : 'var(--best-circle-color)'}, transparent)`,
              borderRadius: '0 9999px 9999px 0',
              pointerEvents: 'none',
            }} />
          </>
        )}
        </div>
      )}
    </div>
  )
}

const Game = () => {
  useUpdateChecker()

  const rawTodayIndex = calculateIndexByDate()
  const initialTodayIndex = rawTodayIndex % wordPairsLength
  const initialYesterdayIndex = (initialTodayIndex - 1 + wordPairsLength) % wordPairsLength
  const puzzleDisplayNumber = rawTodayIndex - LAUNCH_DAYS_SINCE_EPOCH + 1

  const [todayIndex, setTodayIndex] = useState(initialTodayIndex)
  const [yesterdayIndex, setYesterdayIndex] = useState(initialYesterdayIndex)

  const [completionMessage, setCompletionMessage] = useState('')
  const [showCompletionMessage, setShowCompletionMessage] = useState(false)
  const [scoreAnimation, setScoreAnimation] = useState(false) // Blue animation for LETTER hint
  const [scoreAnimationPurple, setScoreAnimationPurple] = useState(false) // Purple animation for WORD boost
  const [scoreAnimationBlack, setScoreAnimationBlack] = useState(false)
  const [scoreDelta, setScoreDelta] = useState(0) // Amount being added (e.g., +100)
  const [showScoreDelta, setShowScoreDelta] = useState(false)
  const [letterDisappearCount, setLetterDisappearCount] = useState(0) // How many letters have disappeared
  const [animateScoreIn, setAnimateScoreIn] = useState(false) // Animate new score into score box
  const [deltaFlyState, setDeltaFlyState] = useState(null) // Controls flying delta animation
  const [bumpExpanded, setBumpExpanded] = useState(false)
  const [hintExpanded, setHintExpanded] = useState(false)
  const [hintIndex, setHintIndex] = useState(-1)

  const [viewInstructions, setViewInstructions] = useState(false)
  const [showTutorial, setShowTutorial] = useState(!storage.hasTutorialCompleted())
  const [isReplayingTutorial, setIsReplayingTutorial] = useState(false)
  const savedGameStateRef = useRef(null)
  const [expandedCircle, setExpandedCircle] = useState('best') // 'score' or 'best'
  const [highestLevel, setHighestLevel] = useState(1)
  const [modalWords, setModalWords] = useState([])
  const [loading, setLoading] = useState(true)
  const [achievedNewBest, setAchievedNewBest] = useState(false)
  const [pendingAnnouncement, setPendingAnnouncement] = useState(null)
  const [wordPairIndex, setWordPairIndex] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  // Display number for whichever puzzle is currently being played (may differ from today's)
  const activePuzzleDisplayNumber = puzzleDisplayNumber - (todayIndex - wordPairIndex + wordPairsLength) % wordPairsLength
  const [progressWord, setProgressWord] = useState('')
  const [progressScore, setProgressScore] = useState(0)
  const [progressCursor, setProgressCursor] = useState(-1)
  const [progressPath, setProgressPath] = useState([])
  const [currentPath, setCurrentPath] = useState([])
  const [currentWord, setCurrentWord] = useState('')
  const [currentLevel, setCurrentLevel] = useState(1)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(999)
  const [cursorPosition, setCursorPosition] = useState(-1)
  const [isGameStarted, setIsGameStarted] = useState(false)
  const [isGameFinished, setIsGameFinished] = useState(false)
  const [winEffect, setWinEffect] = useState(false)
  const [shakeEffect, setShakeEffect] = useState(false)
  const [shakeCount, setShakeCount] = useState(0)
  const [danceEffect, setDanceEffect] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [canVibrate, setCanVibrate] = useState(false)
  const [allSolved, setAllSolved] = useState(false)
  const [solutionsRevealedFlag, setSolutionsRevealedFlag] = useState(
    storage.areSolutionsRevealed(initialTodayIndex)
  )

  const [boostsRemaining, setBoostsRemaining] = useState(4)
  const [hintsUsed, setHintsUsed] = useState(0)

  // Track which path indices were added via hint or boost
  // hintedIndices: Set of indices in currentPath that were hinted
  // boostedIndices: Set of indices in currentPath that were boosted
  const [hintedIndices, setHintedIndices] = useState(new Map()) // Map<wordIndex, letterIndex>
  const [boostedIndices, setBoostedIndices] = useState(new Set())

  // Track saved best path indices (for displaying blue highlighting on best path)
  const [bestHintedIndices, setBestHintedIndices] = useState(new Map())
  const [bestBoostedIndices, setBestBoostedIndices] = useState(new Set())

  const gameContainerRef = useRef(null)
  const levelRef = useRef(null)
  const prevTotalScoreRef = useRef(0)
  const blackAnimationTimeoutRef = useRef(null)
  const scoreAnimateTimeoutRef = useRef(null)
  const errorMessageTimeoutRef = useRef(null)
  const shakeEffectRafRef = useRef(null)
  const completionMessageRef = useRef('')
  const cardRef = useRef(null)
  const scoreContainerRef = useRef(null)

  // Function to load first transformation flag
  const loadFirstTransformationFlag = (puzzleIndex, level) => {
    return storage.hasFirstTransformation(puzzleIndex, level)
  }

  // Function to save first transformation flag
  const saveFirstTransformationFlag = (level, value) => {
    storage.setFirstTransformation(wordPairIndex, level, value)
  }

  const [installPrompt, setInstallPrompt] = useState(null)
  const [isIos, setIsIos] = useState(false)
  const [isAndroid, setIsAndroid] = useState(false)
  const [isSafari, setIsSafari] = useState(false)
  const [isFirefox, setIsFirefox] = useState(false)

  const handleInstallClick = () => {
    if (installPrompt) {
      installPrompt.prompt()
      installPrompt.userChoice.then(() => {
        setInstallPrompt(null)
      })
    }
  }

  const getTotalScore = () => {
      let score = 0
      if (isSolved(1).solved_optimally) {
          score += 100
      } else if (isSolved(1).solved) {
          score += 50
      }
      if (isSolved(2).solved_optimally) {
          score += 200
      } else if (isSolved(2).solved) {
          score += 150
      }
      if (isSolved(3).solved_optimally) {
          score += 300
      } else if (isSolved(3).solved) {
          score += 250
      }
      if (isSolved(4).solved_optimally) {
          score += 400
      } else if (isSolved(4).solved) {
          score += 350
      }
      score -= ((4 - boostsRemaining) * 75)
      // Subtract hint penalties across all levels for this puzzle
      score -= (storage.getTotalHintsUsed(wordPairIndex) * 30)
      return score
  }

  const getLevelScore = (level) => {
    const baseScores = {
      1: { optimal: 100, nonOptimal: 50 },
      2: { optimal: 200, nonOptimal: 150 },
      3: { optimal: 300, nonOptimal: 250 },
      4: { optimal: 400, nonOptimal: 350 }
    }

    const solveStatus = isSolved(level)
    if (!solveStatus.solved) return 0

    let score = solveStatus.solved_optimally
      ? baseScores[level].optimal
      : baseScores[level].nonOptimal

    // Subtract penalties for hints and boosts used at this level
    const hintsUsed = storage.getHintsUsedForLevel(wordPairIndex, level) || 0
    const boostsUsed = storage.getBoostsUsedForLevel(wordPairIndex, level) || 0
    score -= (hintsUsed * 30)
    score -= (boostsUsed * 75)

    return score
  }

  const getBoostsRemainingFromStorage = (puzzleIndex) => {
    return storage.getBoostsRemaining(puzzleIndex)
  }

  // Check if today's solutions have been revealed (score is locked)
  const areTodaysSolutionsRevealed = () => {
    return storage.areSolutionsRevealed(wordPairIndex)
  }

  const handleHint = (e) => {
    if (e) e.stopPropagation()
    if (isGameFinished || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed()) return
    // Prevent activating LETTER if already used for current letter
    if (hintIndex !== -1) return
    // Toggle expansion - do not underline until confirmation
    setHintIndex(-1)
    setHintExpanded(true)
    setBumpExpanded(false) // Close the other if open
  }

  const confirmHint = (e) => {
    if (e) e.stopPropagation()
    // Commit the hint purchase
    setHintsUsed(prev => prev + 1)

    const [startWord, endWord] = getWordPair(currentLevel, wordPairIndex)

    // Only use secondary dictionary if player has already used secondary words
    const useSecondary = pathContainsSecondaryWord(currentPath)

    // Check if player has diverged from AN optimal path (using BFS)
    const divergence = findDivergencePoint(startWord, endWord, currentPath, useSecondary)

    let newPath = currentPath
    let newWord = currentWord
    let newHintedIndices = new Map(hintedIndices)
    let newBoostedIndices = new Set(boostedIndices)

    if (divergence.divergeIndex >= 0) {
      // Player has diverged - revert to last good position
      if (divergence.divergeIndex === 0) {
        // Revert to start
        newPath = []
        newWord = startWord
      } else {
        // Revert to last good position (divergeIndex is in fullPath, so -1 for currentPath)
        newPath = currentPath.slice(0, divergence.divergeIndex)
        newWord = newPath[newPath.length - 1] || startWord
      }

      // Clear hinted/boosted indices beyond the revert point
      newHintedIndices = new Map([...hintedIndices].filter(([i]) => i < newPath.length))
      newBoostedIndices = new Set([...boostedIndices].filter(i => i < newPath.length))

      setCurrentPath(newPath)
      setCurrentWord(newWord)
      setProgressWord(newWord)
      setScore(newPath.length)
      setProgressScore(newPath.length)
      setExpandedCircle('score')
      setModalWords([startWord, ...newPath])
    }

    // Calculate which position was changed in the previous move (if any)
    // This is used to avoid suggesting the same position twice in a row
    let prevChangedPosition = -1
    if (newPath.length > 0) {
      const previousWord = newPath.length === 1 ? startWord : newPath[newPath.length - 2]
      prevChangedPosition = getLetterDiffIndex(previousWord, newWord)
    }

    // Find the next word on an optimal path from current position
    // Try to find an optimal path that doesn't change the same position as the previous move
    let optimalFromHere = useSecondary
      ? findShortestPathAllWordsExcluding(newWord, endWord, prevChangedPosition)
      : findShortestPathExcluding(newWord, endWord, prevChangedPosition)

    // Fallback to regular path finding if excluding the position doesn't find a path
    if (!optimalFromHere) {
      optimalFromHere = useSecondary
        ? findShortestPathAllWords(newWord, endWord)
        : findShortestPath(newWord, endWord)
    }

    let hintLetterIdx = -1

    if (optimalFromHere && optimalFromHere.length > 1) {
      const nextOptimalWord = optimalFromHere[1]
      hintLetterIdx = getLetterDiffIndex(newWord, nextOptimalWord)
    }

    // Update tracking state
    setHintedIndices(newHintedIndices)
    setBoostedIndices(newBoostedIndices)

    // Show the hint (which letter to change)
    setHintIndex(hintLetterIdx)
    if (hintLetterIdx >= 0) {
      setCursorPosition(hintLetterIdx)
      setProgressCursor(hintLetterIdx)
    }

    setHintExpanded(false)
    setScoreAnimation(true)
    setTimeout(() => setScoreAnimation(false), 2000)

    // Persist hint usage for scoring
    storage.incrementHintsUsed(wordPairIndex, currentLevel)

    // Save checkpoint so progress isn't lost on refresh
    storage.saveCheckpoint(wordPairIndex, currentLevel, {
      currentWord: newWord,
      currentPath: newPath,
      score: newPath.length,
      hintedIndices: [...newHintedIndices],
      boostedIndices: [...newBoostedIndices],
      hintIndex: hintLetterIdx
    })
  }

  const cancelHint = (e) => {
    if (e) e.stopPropagation()
    setHintExpanded(false)
  }

  const handleBoost = (e) => {
    if (e) e.stopPropagation()
    if (isGameFinished || boostsRemaining <= 0 || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed()) return
    // Toggle expansion
    setBumpExpanded(true)
    setHintExpanded(false) // Close the other if open
  }

  const confirmBoost = (e) => {
    if (e) e.stopPropagation()
    setCursorPosition(-1)
    // Execute the boost
    // Track if there was an active hint before clearing it
    const hadActiveHint = hintIndex !== -1
    const activeHintLetterIdx = hintIndex

    // Clear any active hint highlight when using a bump
    if (hintIndex !== -1) {
      setHintIndex(-1)
    }

    const [startWord, endWord] = getWordPair(currentLevel, wordPairIndex)

    // Only use secondary dictionary if player has already used secondary words
    const useSecondary = pathContainsSecondaryWord(currentPath)

    // Check if player has diverged from AN optimal path (using BFS)
    const divergence = findDivergencePoint(startWord, endWord, currentPath, useSecondary)

    let workingPath = currentPath
    let workingWord = currentWord
    let newHintedIndices = new Map(hintedIndices)
    let newBoostedIndices = new Set(boostedIndices)

    // If there was an active hint, record it on the current word before boosting
    // pathIndex -1 = start word, otherwise currentPath.length - 1
    if (hadActiveHint) {
      const hintPathIndex = currentPath.length - 1  // -1 if at start word
      newHintedIndices.set(hintPathIndex, activeHintLetterIdx)
    }

    if (divergence.divergeIndex >= 0) {
      // Player has diverged - revert to last good position first
      if (divergence.divergeIndex === 0) {
        workingPath = []
        workingWord = startWord
      } else {
        workingPath = currentPath.slice(0, divergence.divergeIndex)
        workingWord = workingPath[workingPath.length - 1] || startWord
      }

      // Clear hinted/boosted indices beyond the revert point
      newHintedIndices = new Map([...hintedIndices].filter(([i]) => i < workingPath.length))
      newBoostedIndices = new Set([...boostedIndices].filter(i => i < workingPath.length))
    }

    // Find shortest path from current position to end
    const shortestPath = useSecondary
      ? findShortestPathAllWords(workingWord, endWord)
      : findShortestPath(workingWord, endWord)

    if (!shortestPath || shortestPath.length < 2) {
      console.log("error giving boost - no path found")
      setBumpExpanded(false)
      return
    }

    // The next word is the second element in the shortest path
    const nextWord = shortestPath[1]

    if (!nextWord || !isValidWord(nextWord)) {
      console.log("error giving boost - invalid word")
      setBumpExpanded(false)
      return
    }

    // Add the boosted word to the path and track it
    const boostedPath = [...workingPath, nextWord]
    const boostedIndex = boostedPath.length - 1
    newBoostedIndices.add(boostedIndex)

    setCurrentWord(nextWord)
    setProgressWord(nextWord)
    setCurrentPath(boostedPath)
    setHintedIndices(newHintedIndices)
    setBoostedIndices(newBoostedIndices)

    const newScore = boostedPath.length
    setScore(newScore)
    setProgressScore(newScore)

    // Mark that first transformation has been made for this level
    saveFirstTransformationFlag(currentLevel, true)

    if (nextWord === endWord) {
      setIsGameFinished(true)
      setProgressWord('')
      // Update modalWords with the complete path
      setModalWords([startWord, ...boostedPath])
      setExpandedCircle('score')

      // Clear checkpoint on completion
      storage.clearCheckpoint(wordPairIndex, currentLevel)

      // Trigger visual effects immediately to avoid two-frame jank
      const optimal = getOptimalLength(currentLevel)
      const isOptimalSolve = newScore === optimal

      if (best > 0 && newScore < best && !areTodaysSolutionsRevealed()) {
        setProgressPath([...boostedPath.slice(0, -1)])
        setAchievedNewBest(true)
        setBest(newScore)
        setExpandedCircle('best') // Set immediately to prevent flash of collapsed state
        storage.saveScore(wordPairIndex, currentLevel, newScore, boostedPath.slice(0, -1), [...newHintedIndices], [...newBoostedIndices])
        // Update best path indices for display
        setBestHintedIndices(new Map(newHintedIndices))
        setBestBoostedIndices(new Set(newBoostedIndices))

        // Only animate score container black if total score actually increased
        const newTotal = getTotalScore()
        if (newTotal > prevTotalScoreRef.current) {
          const danceDelay = 1500
          const letterDelay = 70
          const wordLength = currentWord.length
          // New timeline: dance -> letters disappear -> delta flies -> score animates
          const allLettersGoneTime = danceDelay + (wordLength * letterDelay)
          const scoreAnimateTime = allLettersGoneTime + 950 + FLY_DURATION

          // Clear any existing black animation timeout
          if (blackAnimationTimeoutRef.current) {
            clearTimeout(blackAnimationTimeoutRef.current)
          }
          // Set up the black animation to trigger when score flies in
          blackAnimationTimeoutRef.current = setTimeout(() => {
            setScoreAnimationBlack(true)
            // Reset after 1 second
            blackAnimationTimeoutRef.current = setTimeout(() => {
              setScoreAnimationBlack(false)
              blackAnimationTimeoutRef.current = null
            }, 1000)
          }, scoreAnimateTime)
        }
      }

      if (isOptimalSolve) {
        const keyboardKeys = document.querySelectorAll('.keyboard-key')
        keyboardKeys.forEach((key) => {
          key.style.backgroundColor = `var(--target-light-color)`
        })
        setWinEffect(true)
      }
      prevTotalScoreRef.current = getTotalScore()
      setDanceEffect(true)

      if (isGameStarted) {
        setProgressScore(0)
        setProgressCursor(-1)
      }
    } else {
      setExpandedCircle('score')
      setModalWords([startWord, ...boostedPath])
      // Save checkpoint so progress isn't lost on refresh
      storage.saveCheckpoint(wordPairIndex, currentLevel, {
        currentWord: nextWord,
        currentPath: boostedPath,
        score: newScore,
        hintedIndices: [...newHintedIndices],
        boostedIndices: [...newBoostedIndices]
      })
    }

    setBoostsRemaining((prev) => prev - 1)

    // Save boosts used
    storage.incrementBoostsUsed(wordPairIndex, currentLevel)
    setBumpExpanded(false)

    // Trigger purple score animation for WORD boost
    setScoreAnimationPurple(true)
    setTimeout(() => setScoreAnimationPurple(false), 2000)
  }

  const cancelBoost = (e) => {
    if (e) e.stopPropagation()
    setBumpExpanded(false)
  }

  const handleClickOutside = () => {
    if (bumpExpanded) {
      setBumpExpanded(false)
    }
    if (hintExpanded) {
      setHintExpanded(false)
    }
  }

  // Add click outside listener for bump/hint buttons
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (bumpExpanded) {
        const bumpButton = document.querySelector('[data-bump-button]')
        if (bumpButton && !bumpButton.contains(e.target)) {
          setBumpExpanded(false)
        }
      }
      if (hintExpanded) {
        const hintButton = document.querySelector('[data-hint-button]')
        if (hintButton && !hintButton.contains(e.target)) {
          setHintExpanded(false)
        }
      }
    }

    if (bumpExpanded || hintExpanded) {
      document.addEventListener('click', handleDocumentClick)
    }

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [bumpExpanded, hintExpanded])

  useEffect(() => {
    const isIosDevice = /iphone|ipad|ipod/i.test(navigator.userAgent)
    const isAndroid = /android/i.test(navigator.userAgent)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    const isFirefox = /firefox/i.test(navigator.userAgent)

    const isStandalone =
      'standalone' in window.navigator && window.navigator.standalone

    setIsIos(isIosDevice)
    setIsAndroid(isAndroid)
    setIsSafari(isSafari)
    setIsFirefox(isFirefox)

    // iOS viewport height fix
    const setVH = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    
    setVH()
    window.addEventListener('resize', setVH)
    window.addEventListener('orientationchange', setVH)
    
    return () => {
      window.removeEventListener('resize', setVH)
      window.removeEventListener('orientationchange', setVH)
    }
  }, [])

  const updateCompletionMessage = (level) => {
    // Check if this specific level was just completed
    if (isSolved(level).solved) {
      let msg = ''

      // Level-specific messages for non-optimal and optimal completion
      const messages = {
        1: { nonOptimal: 'good job!', optimal: 'well done!' },
        2: { nonOptimal: 'excellent!', optimal: 'beautiful!' },
        3: { nonOptimal: 'terrific!', optimal: 'outstanding!' },
        4: { nonOptimal: 'sensational!', optimal: 'perfection!' }
      }

      if (isSolved(level).solved_optimally) {
        msg = messages[level]?.optimal || 'perfect!'
      } else {
        msg = messages[level]?.nonOptimal || 'great work!'
      }
      
      // If all levels are now solved, show the overall completion message
    if (
      isSolved(1).solved &&
      isSolved(2).solved &&
      isSolved(3).solved &&
      isSolved(4).solved
    ) {
        if (areAllLevelsSolvedOptimally()) {
          if (getTotalScore() === 1000) {
            msg = 'superb!!!\nall levels solved optimally\nwith a perfect score!!!'
          } else {
            msg = 'awesome!\nall levels solved optimally!'
          }
        } else {
          // Pick a random celebratory message
          msg = allLevelsSolvedMessages[Math.floor(Math.random() * allLevelsSolvedMessages.length)]
        }
      }

      setCompletionMessage(msg)
      completionMessageRef.current = msg
      setShowCompletionMessage(true)
      // Message hide timing is controlled by the score animation effect
      // to sync with the animation sequence (hides when tab finishes/level changes)
    }
  }

  const showInstructionMessage = () => {
    // Only show this help message once ever - similar to instructions screen
    if (storage.hasSeenKeyboardHelp()) return
    if (showCompletionMessage && completionMessage !== 'first select a letter to change') return
    setCompletionMessage('first select a letter to change')
    completionMessageRef.current = 'first select a letter to change'
    setShowCompletionMessage(true)
    // Message persists until player selects a letter (handled in letter click handler)
  }

  const SolutionsViewer = () => {
    const [selectedLevel, setSelectedLevel] = useState(1) // Track selected level
    const [selectedDateIndex, setSelectedDateIndex] = useState(wordPairIndex) // Selected date index
    const [revealedSolutions, setRevealedSolutions] = useState({}) // Track revealed solutions per date
    const [revealExpanded, setRevealExpanded] = useState(false) // Two-stage reveal button

    const calcInitialOffset = () => {
      const daysAgo = (todayIndex - wordPairIndex + wordPairsLength) % wordPairsLength
      return Math.floor(daysAgo / 8) * 8
    }
    const [pageOffset, setPageOffset] = useState(calcInitialOffset)

    // Reset to level 1 and collapse reveal when date changes
    useEffect(() => {
      setSelectedLevel(1)
      setRevealExpanded(false)
    }, [selectedDateIndex])
    const levels = [1, 2, 3, 4]

    const optimalSolutionRef = useRef(null)
    const playerSolutionRef = useRef(null)

    useEffect(() => {
      // Reset scroll position when selectedLevel changes
      if (optimalSolutionRef.current) {
        optimalSolutionRef.current.scrollLeft = 0
      }
      if (playerSolutionRef.current) {
        playerSolutionRef.current.scrollLeft = 0
      }
    }, [selectedLevel, selectedDateIndex])

    // Setup drag scrolling for desktop
    useEffect(() => {
      const cleanup1 = setupDragScroll(optimalSolutionRef.current)
      const cleanup2 = setupDragScroll(playerSolutionRef.current)
      return () => {
        cleanup1()
        cleanup2()
      }
    }, [])

    // Get available dates for the current page (8 puzzles per page)
    const getAvailableDates = () => {
      const dates = []
      for (let i = 7 + pageOffset; i >= pageOffset; i--) {
        const dateIndex = (todayIndex - i + wordPairsLength) % wordPairsLength
        dates.push({
          index: dateIndex,
          label: `#${puzzleDisplayNumber - i}`
        })
      }
      return dates
    }

    // Mark solutions as viewed for a specific date
    const markSolutionsViewedLocal = (dateIndex) => {
      storage.markSolutionsViewed(dateIndex)
    }

    // Check if solutions have been viewed for a specific date
    const areSolutionsViewedLocal = (dateIndex) => {
      return storage.areSolutionsViewed(dateIndex)
    }

    // Check if solutions have been revealed for a specific date
    const areSolutionsRevealedLocal = (dateIndex) => {
      return storage.areSolutionsRevealed(dateIndex)
    }

    // Mark solutions as revealed for a specific date
    const markSolutionsRevealedLocal = (dateIndex) => {
      storage.markSolutionsRevealed(dateIndex)
      setRevealedSolutions(prev => ({ ...prev, [dateIndex]: true }))
      // Only show lock icon when revealing solutions for the puzzle currently being played
      if (dateIndex === wordPairIndex) {
        setSolutionsRevealedFlag(true)
      }
    }

    // Get current date index
    const getCurrentDateIndex = () => {
      return selectedDateIndex
    }

    // Fetch player's saved solution
    const getPlayerSolution = (level, dateIndex = null) => {
      const targetIndex = dateIndex || getCurrentDateIndex()
      const path = storage.getSavedPath(targetIndex, level)
      const hintedIndices = new Map(storage.getSavedHintedIndices(targetIndex, level) || [])
      const boostedIndices = new Set(storage.getSavedBoostedIndices(targetIndex, level) || [])

      if (path && path.length > 0) {
        return {
          words: [
            getWordPair(level, targetIndex)[0],
            ...path,
            getWordPair(level, targetIndex)[1],
          ],
          hintedIndices,
          boostedIndices,
        }
      }
      // Check if there's a score (might have empty path for direct solutions)
      const score = storage.getBestScore(targetIndex, level)
      if (score !== undefined) {
        return {
          words: [
            getWordPair(level, targetIndex)[0],
            ...path,
            getWordPair(level, targetIndex)[1],
          ],
          hintedIndices,
          boostedIndices,
        }
      }
      return null // Return null if no solution exists
    }

    // Check if player's solution was optimal
    const isPlayerSolutionOptimal = (level, dateIndex = null) => {
      const targetIndex = dateIndex || getCurrentDateIndex()
      const optimal = getOptimalLength(level)
      const status = storage.getSolveStatus(targetIndex, level, optimal)
      return status.solved_optimally
    }

    const renderSolutionPath = (words, hintedIndices = null, boostedIndices = null) => {
      if (!words || words.length === 0) return null

      // Render a single word, optionally with hint/boost coloring
      const renderWord = (word, wordIndex) => {
        // pathIndex: -1 for start word, 0 for first transformation, etc.
        const pathIndex = wordIndex - 1
        const hintedLetterIdx = hintedIndices ? hintedIndices.get(pathIndex) : undefined
        const isBoosted = boostedIndices ? boostedIndices.has(pathIndex) : false

        if (isBoosted && hintedLetterIdx !== undefined) {
          // Boosted word with a hinted letter - purple for boosted, blue for hinted letter
          return word.split('').map((letter, letterIdx) => (
            <span key={letterIdx} style={
              letterIdx === hintedLetterIdx
                ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                : { color: 'var(--uchu-purple)', fontWeight: 'bold' }
            }>{letter}</span>
          ))
        } else if (hintedLetterIdx !== undefined) {
          // Hinted word only - highlight hinted letter in blue
          return word.split('').map((letter, letterIdx) => (
            <span key={letterIdx} style={
              letterIdx === hintedLetterIdx
                ? { color: 'var(--uchu-blue)', fontWeight: 'bold' }
                : {}
            }>{letter}</span>
          ))
        } else if (isBoosted) {
          // Boosted word only - whole word in purple
          return <span style={{ color: 'var(--uchu-purple)', fontWeight: 'bold' }}>{word}</span>
        }
        return word
      }

      // Display horizontal path like score circles: word →¹ word →² word
      return words.map((word, index) => (
        <span
          key={index}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ margin: '0 3px' }}>{renderWord(word, index)}</span>
          {index < words.length - 1 && (
            <span
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '0 3px',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: '0.6em', marginBottom: '-8px' }}>
                {index + 1}
              </span>
              <span style={{ margin: '-5px 0', paddingBottom: '10px' }}>
                →
              </span>
            </span>
          )}
        </span>
      ))
    }

    // Check if user has solved any puzzles for a specific date
    const hasSolvedAnyPuzzles = (dateIndex) => {
      // Check if any level has been solved
      for (let level = 1; level <= 4; level++) {
        const score = storage.getBestScore(dateIndex, level)
        if (score !== undefined && score > 0) {
          return true
        }
      }
      return false
    }

    // Calculate daily score for a specific date using the same logic as getTotalScore
    const calculateDailyScore = (dateIndex) => {
      let score = 0

      // Check each level using the same logic as getTotalScore
      for (let level = 1; level <= 4; level++) {
        const optimalScore = getOptimalLength(level)
        const status = storage.getSolveStatus(dateIndex, level, optimalScore)

        if (status.solved_optimally) {
          if (level === 1) score += 100
          else if (level === 2) score += 200
          else if (level === 3) score += 300
          else if (level === 4) score += 400
        } else if (status.solved) {
          if (level === 1) score += 50
          else if (level === 2) score += 150
          else if (level === 3) score += 250
          else if (level === 4) score += 350
        }
      }

      // Apply boost penalties
      const boostsRemaining = storage.getBoostsRemaining(dateIndex)
      score -= ((4 - boostsRemaining) * 75)

      // Apply hint penalties
      score -= (storage.getTotalHintsUsed(dateIndex) * 30)

      return score
    }

    // Check if user has solved all puzzles optimally for a specific date
    const hasSolvedAllOptimally = (dateIndex) => {
      // Check if all levels have been solved optimally
      for (let level = 1; level <= 4; level++) {
        const optimal = getOptimalLength(level)
        const status = storage.getSolveStatus(dateIndex, level, optimal)
        if (!status.solved_optimally) {
          return false
        }
      }
      return true
    }

    // Load revealed solutions state on component mount
    useEffect(() => {
      const revealed = storage.getAllRevealedPuzzles()
      setRevealedSolutions(revealed)
    }, [])

    // Click outside handler for reveal button
    useEffect(() => {
      const handleDocumentClick = (e) => {
        if (revealExpanded) {
          const revealContainer = document.querySelector('[data-reveal-container]')
          if (revealContainer && !revealContainer.contains(e.target)) {
            setRevealExpanded(false)
          }
        }
      }

      if (revealExpanded) {
        document.addEventListener('click', handleDocumentClick)
      }

      return () => {
        document.removeEventListener('click', handleDocumentClick)
      }
    }, [revealExpanded])

    // Handle revealing solutions (two-stage interaction)
    const handleRevealSolutions = () => {
      if (revealExpanded) {
        // Second click - commit the reveal
        const dateIndex = getCurrentDateIndex()
        markSolutionsRevealedLocal(dateIndex)
        markSolutionsViewedLocal(dateIndex) // Also mark as viewed to prevent retry
        setRevealExpanded(false)
      } else {
        // First click - expand the button
        setRevealExpanded(true)
      }
    }

    return (
      <div style={{ marginTop: '0.5em', textAlign: 'center', width: '100%' }}>
        {/* Date selector with triangle pagination */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.5em auto', maxWidth: '340px' }}>
          {/* Left triangle — navigate into the past */}
          <button
            onClick={() => setPageOffset(p => Math.min(p + 8, puzzleDisplayNumber - 8))}
            style={{
              background: 'none',
              border: 'none',
              cursor: puzzleDisplayNumber - pageOffset > 8 ? 'pointer' : 'default',
              padding: '0 0.4em 0 0',
              fontSize: '1.1em',
              color: 'var(--uchu-yin)',
              visibility: puzzleDisplayNumber - pageOffset > 8 ? 'visible' : 'hidden',
              flexShrink: 0,
            }}
          >
            ◀
          </button>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '0.25em',
              flex: 1,
            }}
          >
            {getAvailableDates().map((dateInfo) => {
              const hasSolved = hasSolvedAnyPuzzles(dateInfo.index)
              const allOptimal = hasSolvedAllOptimally(dateInfo.index)

              return (
                <button
                  key={dateInfo.index}
                  onClick={() => setSelectedDateIndex(dateInfo.index)}
                  style={{
                    padding: '0.25em 0.5em',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: selectedDateIndex === dateInfo.index ? 'var(--uchu-red)' : 'var(--uchu-yang)',
                    color: selectedDateIndex === dateInfo.index ? 'var(--uchu-yang)' : 'var(--uchu-red)',
                    border: 'none',
                    fontWeight: 'bold',
                    fontSize: '0.8em',
                    position: 'relative',
                  }}
                >
                  {dateInfo.label}
                  {hasSolved && (
                    <div
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: allOptimal ? 'var(--target-color)' : 'var(--best-circle-color)',
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                      }}
                    />
                  )}
                </button>
              )
            })}
          </div>
          {/* Right triangle — navigate toward today */}
          <button
            onClick={() => setPageOffset(p => Math.max(p - 8, 0))}
            style={{
              background: 'none',
              border: 'none',
              cursor: pageOffset > 0 ? 'pointer' : 'default',
              padding: '0 0 0 0.4em',
              fontSize: '1.1em',
              color: 'var(--uchu-yin)',
              visibility: pageOffset > 0 ? 'visible' : 'hidden',
              flexShrink: 0,
            }}
          >
            ▶
          </button>
        </div>

        {/* Daily Score Display */}
        {(() => {
          const dailyScore = calculateDailyScore(selectedDateIndex)
          const allOptimal = hasSolvedAllOptimally(selectedDateIndex)
          const hasAnySolved = hasSolvedAnyPuzzles(selectedDateIndex)
          return (
          <div
            style={{
                margin: '0.5em 0',
              padding: '0.5em',
                backgroundColor: hasAnySolved 
                  ? (allOptimal ? 'var(--target-color)' : 'var(--uchu-gray-2)')
                  : 'var(--uchu-gray-2)',
              borderRadius: '4px',
                fontSize: '1em',
                fontWeight: 'bold',
                color: hasAnySolved 
                  ? (allOptimal ? 'var(--uchu-yin)' : 'var(--uchu-gray-4)')
                  : 'var(--uchu-dark-gray)',
                textAlign: 'center',
              }}
            >
              Score: {hasAnySolved ? `${dailyScore} out of 1000` : 'Unsolved'}
          </div>
          )
        })()}

        {/* Play puzzle button — past puzzles always; today's puzzle only when currently playing a different puzzle */}
        {(selectedDateIndex !== todayIndex || wordPairIndex !== todayIndex) && (
          <button
            onClick={() => {
              const lastLevel = storage.getLastActiveLevel(selectedDateIndex) || 1
              initGame(selectedDateIndex, false, lastLevel)
              setViewInstructions(false)
            }}
            style={{
              width: '100%',
              padding: '0.5em 1em',
              height: '2.5em',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: 'var(--uchu-red)',
              color: 'var(--uchu-yang)',
              fontWeight: 'bold',
              fontSize: '0.9em',
              marginBottom: '0.5em',
            }}
          >
            Play #{puzzleDisplayNumber - (todayIndex - selectedDateIndex + wordPairsLength) % wordPairsLength}
          </button>
        )}

        {/* Reveal button for today only - hidden if all levels solved optimally */}
        {selectedDateIndex === todayIndex && !areSolutionsRevealedLocal(todayIndex) && !hasSolvedAllOptimally(todayIndex) && (
          <div data-reveal-container style={{ display: 'flex', width: '100%' }}>
            {/* X button for reveal */}
            <button
              onClick={() => setRevealExpanded(false)}
              style={{
                flex: revealExpanded ? '0 0 2.5em' : '0 0 0',
                minWidth: 0,
                width: revealExpanded ? '2.5em' : 0,
                whiteSpace: 'nowrap',
                height: '2.5em',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'var(--uchu-red)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                padding: revealExpanded ? '0 0.6em' : 0,
                marginRight: revealExpanded ? '0.5em' : 0,
                fontSize: '0.9em',
                fontWeight: 'bold',
                color: 'white',
                overflow: 'hidden',
                opacity: revealExpanded ? 1 : 0,
              }}
            >
              <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            {/* Main reveal button */}
            <button
              data-reveal-button
              onClick={revealExpanded ? undefined : handleRevealSolutions}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '0.5em 1em',
                height: '2.5em',
                borderRadius: '4px',
                border: 'none',
                cursor: revealExpanded ? 'default' : 'pointer',
                backgroundColor: 'var(--uchu-light-red)',
                color: revealExpanded ? 'var(--uchu-dark-red)' : 'var(--uchu-red)',
                fontWeight: 'bold',
                fontSize: '0.9em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {revealExpanded ? 'Reveal and lock score?' : 'Reveal Today\'s Solutions'}
            </button>
            {/* Checkmark button for reveal */}
            <button
              onClick={handleRevealSolutions}
              style={{
                flex: revealExpanded ? '0 0 2.5em' : '0 0 0',
                minWidth: 0,
                width: revealExpanded ? '2.5em' : 0,
                whiteSpace: 'nowrap',
                height: '2.5em',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'var(--uchu-green)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                padding: revealExpanded ? '0 0.6em' : 0,
                marginLeft: revealExpanded ? '0.5em' : 0,
                fontSize: '0.9em',
                fontWeight: 'bold',
                color: 'white',
                overflow: 'hidden',
                opacity: revealExpanded ? 1 : 0,
              }}
            >
              <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </button>
          </div>
        )}

        {/* Reveal Solutions button for past puzzles */}
        {selectedDateIndex !== todayIndex && !areSolutionsRevealedLocal(selectedDateIndex) && !hasSolvedAllOptimally(selectedDateIndex) && (
          <div data-reveal-container style={{ display: 'flex', width: '100%' }}>
            {/* X button for reveal */}
            <button
              onClick={() => setRevealExpanded(false)}
              style={{
                flex: revealExpanded ? '0 0 2.5em' : '0 0 0',
                minWidth: 0,
                width: revealExpanded ? '2.5em' : 0,
                whiteSpace: 'nowrap',
                height: '2.5em',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'var(--uchu-red)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                padding: revealExpanded ? '0 0.6em' : 0,
                marginRight: revealExpanded ? '0.5em' : 0,
                fontSize: '0.9em',
                fontWeight: 'bold',
                color: 'white',
                overflow: 'hidden',
                opacity: revealExpanded ? 1 : 0,
              }}
            >
              <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            {/* Main reveal button */}
            <button
              data-reveal-button
              onClick={revealExpanded ? undefined : handleRevealSolutions}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '0.5em 1em',
                height: '2.5em',
                borderRadius: '4px',
                border: 'none',
                cursor: revealExpanded ? 'default' : 'pointer',
                backgroundColor: 'var(--uchu-light-red)',
                color: revealExpanded ? 'var(--uchu-dark-red)' : 'var(--uchu-red)',
                fontWeight: 'bold',
                fontSize: '0.9em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {revealExpanded ? 'Reveal and lock score?' : 'Reveal Solutions'}
            </button>
            {/* Checkmark button for reveal */}
            <button
              onClick={handleRevealSolutions}
              style={{
                flex: revealExpanded ? '0 0 2.5em' : '0 0 0',
                minWidth: 0,
                width: revealExpanded ? '2.5em' : 0,
                whiteSpace: 'nowrap',
                height: '2.5em',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'var(--uchu-green)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                padding: revealExpanded ? '0 0.6em' : 0,
                marginLeft: revealExpanded ? '0.5em' : 0,
                fontSize: '0.9em',
                fontWeight: 'bold',
                color: 'white',
                overflow: 'hidden',
                opacity: revealExpanded ? 1 : 0,
              }}
            >
              <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </button>
          </div>
        )}

        {/* Level tabs - shown only when solutions are unlocked */}
        {(areSolutionsRevealedLocal(selectedDateIndex) || hasSolvedAllOptimally(selectedDateIndex)) && (
          <div
            style={{
              marginTop: '0.75em',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
          {levels.map((level) => (
            <button
              key={level}
              onClick={() => setSelectedLevel(level)}
              style={{
                padding: '0.25em 0.5em',
                marginRight: '0.5em',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor:
                  selectedLevel === level
                    ? 'var(--uchu-red)'
                    : 'var(--uchu-light-red)',
                color:
                  selectedLevel === level
                    ? 'var(--uchu-yang)'
                    : 'var(--uchu-red)',
                border: 'none',
                fontWeight: 'bold',
                fontSize: '0.825em',
              }}
            >
              level {level}
            </button>
          ))}
          </div>
        )}

        {/* Display Solutions - shown only when solutions are unlocked */}
        {(areSolutionsRevealedLocal(selectedDateIndex) || hasSolvedAllOptimally(selectedDateIndex)) && (() => {
          const playerSolution = getPlayerSolution(selectedLevel, selectedDateIndex)
          const optimalSolution = getOptimalPath(selectedLevel, selectedDateIndex)

          return (
          <div style={{ marginTop: '0.75em' }}>
            {/* Optimal Solution - shown first */}
            <div>
              <h4 style={{ marginTop: '0.25em', marginBottom: '0.25em' }}>a solution</h4>
              <div style={{ position: 'relative' }}>
                <div
                  ref={optimalSolutionRef}
                  onScroll={(e) => {
                    if (playerSolutionRef.current && !playerSolutionRef.current.dataset.syncing) {
                      optimalSolutionRef.current.dataset.syncing = 'true'
                      playerSolutionRef.current.scrollLeft = e.target.scrollLeft
                      requestAnimationFrame(() => {
                        if (optimalSolutionRef.current) optimalSolutionRef.current.dataset.syncing = ''
                      })
                    }
                  }}
                  style={{
                    borderRadius: '9999px',
                    backgroundColor: 'var(--target-color)',
                    padding: '0.25em 0.75em',
                    fontFamily: 'Itim',
                    fontSize: '1.25em',
                    height: '2em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    boxSizing: 'border-box',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {renderSolutionPath(optimalSolution)}
                </div>
                {/* Fade overlays for scroll indication */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '1.25em',
                  background: 'linear-gradient(to right, var(--target-color), transparent)',
                  borderRadius: '9999px 0 0 9999px',
                  pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '1.25em',
                  background: 'linear-gradient(to left, var(--target-color), transparent)',
                  borderRadius: '0 9999px 9999px 0',
                  pointerEvents: 'none',
                }} />
              </div>
            </div>

            {/* Player Solution - shown second */}
            {playerSolution?.words && (
            <div style={{ marginTop: '0.5em' }}>
              <h4
                style={{
                  color: 'var(--player-color)',
                  marginTop: '0.25em',
                  marginBottom: '0.25em',
                }}
              >
                your solution
              </h4>
              <div style={{ position: 'relative' }}>
                <div
                  ref={playerSolutionRef}
                  onScroll={(e) => {
                    if (optimalSolutionRef.current && !optimalSolutionRef.current.dataset.syncing) {
                      playerSolutionRef.current.dataset.syncing = 'true'
                      optimalSolutionRef.current.scrollLeft = e.target.scrollLeft
                      requestAnimationFrame(() => {
                        if (playerSolutionRef.current) playerSolutionRef.current.dataset.syncing = ''
                      })
                    }
                  }}
                  style={{
                    borderRadius: '9999px',
                    backgroundColor: isPlayerSolutionOptimal(selectedLevel) ? 'var(--target-color)' : 'var(--score-circle-color)',
                    padding: '0.25em 0.75em',
                    fontFamily: 'Itim',
                    fontSize: '1.25em',
                    height: '2em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    boxSizing: 'border-box',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {renderSolutionPath(playerSolution.words, playerSolution.hintedIndices, playerSolution.boostedIndices)}
                </div>
                {/* Fade overlays for scroll indication */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '1.25em',
                  background: `linear-gradient(to right, ${isPlayerSolutionOptimal(selectedLevel) ? 'var(--target-color)' : 'var(--score-circle-color)'}, transparent)`,
                  borderRadius: '9999px 0 0 9999px',
                  pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '1.25em',
                  background: `linear-gradient(to left, ${isPlayerSolutionOptimal(selectedLevel) ? 'var(--target-color)' : 'var(--score-circle-color)'}, transparent)`,
                  borderRadius: '0 9999px 9999px 0',
                  pointerEvents: 'none',
                }} />
              </div>
            </div>
            )}
          </div>
          )
        })()}

      </div>
    )
  }

  const handleScoreCircleClick = () => {
    setExpandedCircle('score')
    setModalWords([
      getWordPair(currentLevel, wordPairIndex)[0],
      ...currentPath,
    ])
  }

  const handleBestCircleClick = () => {
    setExpandedCircle((prev) => {
      return 'best'
    })
    setModalWords([
      getWordPair(currentLevel, wordPairIndex)[0],
      ...progressPath,
      getWordPair(currentLevel, wordPairIndex)[1],
    ])
  }

  const toggleViewInstructions = () => {
    if (viewInstructions) {
      storage.markAsPlayed()
    }
    setViewInstructions(!viewInstructions)
  }

  const showCurrentModal = () => {
    setModalWords([
      getWordPair(currentLevel, wordPairIndex)[0],
      ...currentPath,
    ])
  }

  const handleWordClick = (index, progress) => {
    // Hide the "all levels solved" message when user interacts with cards
    if (allLevelsSolvedMessages.includes(completionMessage)) {
      setShowCompletionMessage(false)
    }
    const startWord = getWordPair(currentLevel, wordPairIndex)[0]
    if (index === 0) {
      // Just revert to starting word without resetting the first transformation flag
      setCurrentPath([])
      setProgressWord('')
      setCurrentWord(startWord)
      setScore(0)
      setProgressScore(0)

      // Check if there was a hint used on the start word (pathIndex -1)
      const startWordHint = hintedIndices.get(-1)
      if (startWordHint !== undefined) {
        // Restore the hint that was used on the start word
        setHintIndex(startWordHint)
        setCursorPosition(startWordHint)
        // Keep only the start word hint, clear others
        setHintedIndices(new Map([[-1, startWordHint]]))
      } else {
        // Clear all hinted tracking when reverting to start
        setHintedIndices(new Map())
        setHintIndex(-1)
      }
      setBoostedIndices(new Set())

      // Ensure first transformation flag is preserved when clicking from best circle
      if (progress && !loadFirstTransformationFlag(wordPairIndex, currentLevel)) {
        saveFirstTransformationFlag(currentLevel, true)
      }
      // Expand score pill and show only the first word
      setExpandedCircle('score')
      setModalWords([startWord])
      // Save checkpoint with reverted state
      storage.saveCheckpoint(wordPairIndex, currentLevel, {
        currentWord: startWord,
        currentPath: [],
        score: 0,
        hintedIndices: startWordHint !== undefined ? [[-1, startWordHint]] : [],
        boostedIndices: [],
        hintIndex: startWordHint !== undefined ? startWordHint : -1
      })
      return
    }

    if (isGameFinished) {
      initGame(wordPairIndex)
    }

    const newPath = (progress ? progressPath : currentPath).slice(0, index)
    const selectedWord = newPath[newPath.length - 1] || ''

    setCurrentPath(newPath)
    setProgressWord(selectedWord)
    setCurrentWord(selectedWord)

    // Clear hinted/boosted indices beyond the new path length
    const newHintedIndices = new Map([...hintedIndices].filter(([i]) => i < newPath.length))
    const newBoostedIndices = new Set([...boostedIndices].filter(i => i < newPath.length))
    setHintedIndices(newHintedIndices)
    setBoostedIndices(newBoostedIndices)

    // Restore hintIndex if the current word (now last in path) had a stored hint
    // The current word's pathIndex is newPath.length - 1
    const currentWordPathIndex = newPath.length - 1
    const storedHint = newHintedIndices.get(currentWordPathIndex)
    if (storedHint !== undefined) {
      setHintIndex(storedHint)
      setCursorPosition(storedHint)
    } else {
      setHintIndex(-1)
    }

    const newScore = newPath.length
    setScore(newScore)
    setProgressScore(newScore)
    // Expand score pill and update its content to reflect the selected point in path
    setExpandedCircle('score')
    setModalWords([startWord, ...newPath])

    // Save checkpoint with updated state (including restored hintIndex if any)
    storage.saveCheckpoint(wordPairIndex, currentLevel, {
      currentWord: selectedWord,
      currentPath: newPath,
      score: newScore,
      hintedIndices: [...newHintedIndices],
      boostedIndices: [...newBoostedIndices],
      hintIndex: storedHint !== undefined ? storedHint : -1
    })
  }

  useEffect(() => {
    const gameData = storage.getGameData()
    const storedVersion = storage.getStoredVersion()
    const played = storage.hasPlayed()

    // If no data exists, initialize fresh with tutorial demo index
    if (showTutorial) {
      if (!isReplayingTutorial) {
        // First time tutorial - initialize storage
        storage.initializeStorage(version, false)
      }
      initGame(TUTORIAL_WORD_INDEX)
      return
    }

    // Get highest unlocked level
    const loadedHighestLevel = storage.getHighestUnlockedLevel(todayIndex)
    setHighestLevel(loadedHighestLevel)

    // If version changed, reset but preserve played status
    if (!storedVersion || storedVersion !== version) {
      storage.initializeStorage(version, true)
      initGame(todayIndex, true, 1)
    }

    if (played) {
      setViewInstructions(false)
    }

    // Check if any level has a checkpoint (from hint/boost)
    const levelWithCheckpoint = storage.findLevelWithCheckpoint(todayIndex)
    if (levelWithCheckpoint) {
      // Restore from checkpoint - don't force reset
      initGame(todayIndex, false, levelWithCheckpoint)
    } else {
      // No checkpoint, start fresh at highest unlocked level
      initGame(todayIndex, true, storage.getLastActiveLevel(todayIndex))
    }

    // Show the first unseen feature announcement to returning players
    if (played) {
      const unseen = ANNOUNCEMENTS.find(a => !storage.hasSeenAnnouncement(a.id))
      if (unseen) setPendingAnnouncement(unseen)
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      const newIndex = calculateIndexByDate() % wordPairsLength
      if (newIndex !== todayIndex) {
        setTodayIndex(newIndex)
        setSolutionsRevealedFlag(storage.areSolutionsRevealed(newIndex))
        setShowCompletionMessage(false)
        setCompletionMessage('')
        completionMessageRef.current = ''
        initGame(newIndex, true, 1)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [todayIndex])

  useEffect(() => {
    // Don't auto-transition if no completion effect is active
    if (!winEffect && !danceEffect) {
      return
    }

    // Find the next level that needs work (not yet solved at all)
    // Note: Once all levels are solved, we don't auto-move to sub-optimal levels
    // The player can choose to replay those levels themselves
    const findNextIncompleteLevel = () => {
      // Check if all levels are solved (regardless of optimality)
      const allLevelsSolved = isSolved(1).solved && isSolved(2).solved &&
                               isSolved(3).solved && isSolved(4).solved
      if (allLevelsSolved) {
        // All levels solved - don't auto-move, let player decide
        return null
      }

      // First, check levels after current level
      for (let level = currentLevel + 1; level <= 4; level++) {
        const status = isSolved(level)
        if (!status.solved) {
          return level
        }
      }
      // Then wrap around and check levels before current level
      for (let level = 1; level < currentLevel; level++) {
        const status = isSolved(level)
        if (!status.solved) {
          return level
        }
      }
      // All levels are solved, stay on current level
      return null
    }

    const nextLevel = findNextIncompleteLevel()

    // Compute delta to determine which timeline to use
    const newTotal = getTotalScore()
    const delta = newTotal - prevTotalScoreRef.current

    // Animation sequence timing:
    // If delta > 0: dance → letters disappear → delta flies up → tab descends → level changes
    // If delta = 0: dance → old behavior (no fly animation)
    const danceDelay = 1500
    const letterDelay = 70
    const wordLength = currentWord.length

    let transitionDelay

    if (delta > 0) {
      // New timeline with fly animation
      const allLettersGoneTime = danceDelay + (wordLength * letterDelay)
      const flyStartTime = allLettersGoneTime + 950
      const flyArrivalTime = flyStartTime + FLY_DURATION
      const tabStartTime = flyArrivalTime + 300
      const tabEndTime = tabStartTime + 600
      transitionDelay = tabEndTime
    } else {
      // Old timeline without fly animation
      const transformEnd = danceDelay + (wordLength * letterDelay) + 200
      const tabStart = transformEnd + 1000
      const tabEnd = tabStart + 600
      transitionDelay = tabEnd
    }

    // If there's a next level to work on, transition to it
    if (nextLevel !== null) {
      const timeout = setTimeout(
        () => {
          // During tutorial, always use handleUnlockLevel to update highestLevel state
          // Otherwise check storage to see if level is already unlocked
          if (showTutorial || !hasUnlockedLevel(nextLevel)) {
            handleUnlockLevel(nextLevel)
          } else {
            switchToLevel(nextLevel)
          }
        },
        transitionDelay
      )

      return () => clearTimeout(timeout)
    } else {
      // All levels are solved - reset game after celebration so player can retry
      const timeout = setTimeout(
        () => {
          // Call initGame which resets currentWord to start word BEFORE setting
          // isGameFinished to false, preventing the completion effect from re-triggering
          initGame(wordPairIndex, true, currentLevel)
        },
        transitionDelay
      )
      return () => clearTimeout(timeout)
    }
  }, [best, winEffect, danceEffect, currentWord.length])

  // Score reveal animation sequence when level is completed
  // Sequence: dance → letters disappear → +X appears → flies to score → tab descends → message hides/level changes
  useEffect(() => {
    if (danceEffect) {
      // Animation timing constants
      const danceDelay = 1500
      const letterDelay = 70 // ms between each letter disappearing
      const wordLength = currentWord.length

      // Timeline:
      // T=0: Dance + message appear
      // T=danceDelay: Letters start disappearing (only if delta > 0)
      // T=allLettersGone: All letters gone, center delta appears
      // T=allLettersGone + 950: Center delta pop-in complete, measure positions and start fly
      // T=flyArrival: Delta arrives at score container, update score, trigger black animation
      // T=flyArrival + 300: Tab starts descending
      // T=tabEnd: Tab finishes, message hides, level changes
      const allLettersGoneTime = danceDelay + (wordLength * letterDelay)
      const flyStartTime = allLettersGoneTime + 950
      const flyArrivalTime = flyStartTime + FLY_DURATION
      const tabStartTime = flyArrivalTime + 300
      const tabEndTime = tabStartTime + 600

      const timeouts = []

      // Score animation only happens if there's a delta
      const newTotal = getTotalScore()
      const delta = newTotal - prevTotalScoreRef.current

      if (delta > 0) {
        setScoreDelta(delta)

        // Reset states
        setLetterDisappearCount(0)
        setAnimateScoreIn(false)
        setShowScoreDelta(false)
        setDeltaFlyState(null)

        // After dance completes, disappear all letters at once (CSS transition-delay staggers visually)
        const disappearTimeout = setTimeout(() => {
          setLetterDisappearCount(wordLength)
        }, danceDelay)
        timeouts.push(disappearTimeout)

        // After all letters gone, measure positions and render delta at card center
        const centerDeltaTimeout = setTimeout(() => {
          if (cardRef.current && scoreContainerRef.current) {
            const cardRect = cardRef.current.getBoundingClientRect()
            const targetRect = scoreContainerRef.current.getBoundingClientRect()

            // Render element at card center with pop-in animation
            setDeltaFlyState({
              sourceTop: `${cardRect.top + cardRect.height / 2}px`,
              sourceLeft: `${cardRect.left + cardRect.width / 2}px`,
              targetTop: `${targetRect.bottom}px`,
              targetLeft: `${targetRect.left}px`,
              flying: false,
            })
          }
        }, allLettersGoneTime)
        timeouts.push(centerDeltaTimeout)

        // After pop-in animation completes (400ms), start fly
        const flyStartTimeout = setTimeout(() => {
          setDeltaFlyState(prev => prev ? { ...prev, flying: true } : null)
        }, flyStartTime)
        timeouts.push(flyStartTimeout)

        // At fly arrival, update score and trigger animations
        // Use ref-based timeout so it survives level transitions
        if (scoreAnimateTimeoutRef.current) {
          clearTimeout(scoreAnimateTimeoutRef.current)
        }
        scoreAnimateTimeoutRef.current = setTimeout(() => {
          setDeltaFlyState(null)
          prevTotalScoreRef.current = newTotal
          setAnimateScoreIn(true)
          // Reset animation states after score animation completes
          scoreAnimateTimeoutRef.current = setTimeout(() => {
            setAnimateScoreIn(false)
            scoreAnimateTimeoutRef.current = null
          }, 1000)
        }, flyArrivalTime)

        // Hide completion message when tab finishes
        // Exception: "all levels solved" messages persist until player interacts
        const messageHideTimeout = setTimeout(() => {
          if (!isAllLevelsSolvedMessage(completionMessageRef.current)) {
            setShowCompletionMessage(false)
          }
        }, tabEndTime)
        timeouts.push(messageHideTimeout)
      } else {
        // No score delta - use old timeline
        const transformEnd = danceDelay + (wordLength * letterDelay) + 200
        const tabStart = transformEnd + 1000
        const tabEnd = tabStart + 600

        const messageHideTimeout = setTimeout(() => {
          if (!isAllLevelsSolvedMessage(completionMessageRef.current)) {
            setShowCompletionMessage(false)
          }
        }, tabEnd)
        timeouts.push(messageHideTimeout)
      }

      return () => timeouts.forEach(t => clearTimeout(t))
    }
  }, [danceEffect])

  // Cancel any pending error-shake rAF/timeout the moment level completion is
  // detected. useLayoutEffect fires before rAF callbacks, so cancelAnimationFrame
  // is effective here (unlike in useEffect which fires after paint).
  useLayoutEffect(() => {
    const [startWord, endWord] = getWordPair(currentLevel, wordPairIndex)
    if (currentWord !== endWord) {
      // Dismiss sticky all-levels-solved message when the player actually moves
      // (ignore resets back to the start word, which happen via initGame)
      if (currentWord !== startWord && isAllLevelsSolvedMessage(completionMessageRef.current)) {
        setShowCompletionMessage(false)
        completionMessageRef.current = ''
      }
      return
    }

    if (shakeEffectRafRef.current) {
      cancelAnimationFrame(shakeEffectRafRef.current)
      shakeEffectRafRef.current = null
    }
    if (errorMessageTimeoutRef.current) {
      clearTimeout(errorMessageTimeoutRef.current)
      errorMessageTimeoutRef.current = null
    }
    setShakeEffect(false)
    setShowCompletionMessage(false)
  }, [currentWord, currentLevel, wordPairIndex])

  useEffect(() => {
    // Skip if effects were already triggered by keyboard/boost handler
    if (winEffect || danceEffect) {
      return
    }

    // Don't re-trigger effects if game is already finished
    if (isGameFinished) {
      return
    }

    const endWord = getWordPair(currentLevel, wordPairIndex)[1]
    if (currentWord !== endWord) {
      return
    }

    if (!score) {
      return
    }

    setIsGameFinished(true)

    setShakeEffect(false)
    setShowCompletionMessage(false)

    const optimal = getOptimalLength(currentLevel)
    if (score === optimal) {
      const keyboardKeys = document.querySelectorAll('.keyboard-key')
      keyboardKeys.forEach((key) => {
        key.style.backgroundColor = `var(--target-light-color)`
      })
      setWinEffect(true)
    }
    setDanceEffect(true)

    if (isGameStarted) {
      setProgressScore(0)
      setProgressCursor(-1)
    }
  }, [currentLevel, currentWord, wordPairIndex, winEffect, danceEffect, isGameFinished])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    // Only trigger completion effects when the game was just finished (not when loading a level)
    if (best !== 999 && score !== 0 && isGameFinished) {
      // Always show completion message when level is finished
      // This allows showing congratulatory messages when replaying to beat optimally
      updateCompletionMessage(currentLevel)
    }
  }, [best, isGameFinished])

  // When a new best is achieved, auto-expand the best circle
  useEffect(() => {
    if (achievedNewBest) {
      handleBestCircleClick()
    }
  }, [achievedNewBest])

  useEffect(() => {
    if (shakeEffect && canVibrate) {
      navigator.vibrate([50, 175, 50, 175, 50])
    }
  }, [shakeEffect, canVibrate])

  useEffect(() => {
    if (loading) return
    // Don't override game state during tutorial - onComplete handles transition
    if (showTutorial) return
    // On initial load, check if any level has a checkpoint
    const startLevel = storage.getLastActiveLevel(todayIndex)
    initGame(todayIndex, false, startLevel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayIndex, loading])

  const initGame = (index, reset = false, level = currentLevel) => {
    if (gameContainerRef.current) {
      gameContainerRef.current.focus()
    }

    const start = getWordPair(level, index)[0]

    // Check for checkpoint (saved progress from hint/boost)
    const checkpoint = storage.getCheckpoint(index, level)

    if (checkpoint && !reset) {
      // Restore from checkpoint
      setCurrentPath(checkpoint.currentPath || [])
      setCurrentWord(checkpoint.currentWord || start)
      setScore(checkpoint.score || 0)
      setProgressScore(checkpoint.score || 0)
      setProgressWord(checkpoint.currentWord || start)
      setHintedIndices(new Map(checkpoint.hintedIndices || []))
      setBoostedIndices(new Set(checkpoint.boostedIndices || []))
      if (checkpoint.hintIndex !== undefined && checkpoint.hintIndex >= 0) {
        setHintIndex(checkpoint.hintIndex)
        setCursorPosition(checkpoint.hintIndex)
        setProgressCursor(checkpoint.hintIndex)
      } else {
        setHintIndex(-1)
        setCursorPosition(-1)
        setProgressCursor(-1)
      }
      // Restore first transformation flag if checkpoint has progress
      if (checkpoint.currentPath && checkpoint.currentPath.length > 0) {
        saveFirstTransformationFlag(level, true)
      }
    } else {
      // No checkpoint - start fresh on this level
      setCurrentPath([])
      setCurrentWord(start)
      setScore(0)
      setProgressScore(0)
      setProgressWord(start)
      setProgressCursor(-1)
      setCursorPosition(-1)
      setHintedIndices(new Map())
      setBoostedIndices(new Set())
      setHintIndex(-1)
      // Reset first transformation flag when starting fresh
      saveFirstTransformationFlag(level, false)
    }

    setWordPairIndex(index)
    setSolutionsRevealedFlag(storage.areSolutionsRevealed(index))
    setAllSolved(
      isSolved(1).solved &&
        isSolved(2).solved &&
        isSolved(3).solved &&
        isSolved(4).solved
    )
    setHighestLevel(reset ? 1 : storage.getHighestUnlockedLevel(index))
    setCurrentLevel(level)
    storage.saveLastActiveLevel(index, level)
    setBoostsRemaining(getBoostsRemainingFromStorage(index))
    setIsGameStarted(true)
    setIsGameFinished(false)
    if (index !== wordPairIndex) {
      setShowCompletionMessage(false)
      setCompletionMessage('')
      completionMessageRef.current = ''
    }
    setShakeEffect(false)
    setHintExpanded(false)
    setBumpExpanded(false)
    setProgressPath([])
    setAchievedNewBest(false)
    setExpandedCircle('best')

    // Always clear win effects when loading a level
    setWinEffect(false)
    setDanceEffect(false)
    setLetterDisappearCount(0)
    setAnimateScoreIn(false)
    setDeltaFlyState(null)
    // Note: Don't reset scoreAnimationBlack here - let its own timeout handle it
    document.body.style.backgroundColor = 'var(--uchu-yang)'
    const keyboardKeys = document.querySelectorAll('.keyboard-key')
    keyboardKeys.forEach((key) => {
      key.style.backgroundColor = `var(--uchu-light-gray-transparent)`
    })

    const parsedBest = storage.getBestScore(index, level)
    const parsedPath = storage.getSavedPath(index, level)

    if (parsedBest !== undefined && parsedBest >= getOptimalLength(level) - 1) {
      setBest(parsedBest)
      setProgressPath(parsedPath)
      // Populate best path display using stored path
      const startWord = getWordPair(level, index)[0]
      const endWord = getWordPair(level, index)[1]
      setModalWords([startWord, ...parsedPath, endWord])
      // Load saved hinted/boosted indices for best path display
      const savedHinted = storage.getSavedHintedIndices(index, level)
      const savedBoosted = storage.getSavedBoostedIndices(index, level)
      setBestHintedIndices(new Map(savedHinted))
      setBestBoostedIndices(new Set(savedBoosted))
    } else {
      setBest(999)
      // Show default optimal path from data set
      setModalWords(getOptimalPath(level, index))
      // Clear best path indices when no best exists
      setBestHintedIndices(new Map())
      setBestBoostedIndices(new Set())
    }

    // If restoring a checkpoint with in-progress path, show score circle
    if (checkpoint && !reset && checkpoint.currentPath && checkpoint.currentPath.length > 0) {
      setExpandedCircle('score')
      setModalWords([start, ...checkpoint.currentPath])
    }

    // Initialize the previous total score ref for delta animation
    prevTotalScoreRef.current = getTotalScore()
    setLoading(false)
  }

  const solveStatuses = useMemo(() => {
    const wordIndex = isGameStarted ? wordPairIndex : yesterdayIndex
    return [1, 2, 3, 4].reduce((acc, level) => {
      acc[level] = storage.getSolveStatus(wordIndex, level, getOptimalLength(level))
      return acc
    }, {})
  }, [isGameStarted, wordPairIndex, yesterdayIndex, best])

  const isSolved = (level) => solveStatuses[level] || { solved: false, solved_optimally: false }

  const areAnyLevelsSolved = () => {
    let solved = false
    for (let i = 1; i <= highestLevel; i++) {
      solved = solved || isSolved(i).solved
    }
    return solved
  }

  const areAllLevelsSolved = () => {
    for (let i = 1; i <= 4; i++) {
      if (!isSolved(i).solved) {
        return false
      }
    }
    return true
  }

  const areAllLevelsSolvedOptimally = () => {
    return (
      isSolved(1).solved_optimally &&
      isSolved(2).solved_optimally &&
      isSolved(3).solved_optimally &&
      isSolved(4).solved_optimally
    )
  }

  const hasUnlockedLevel = (level) => {
    return storage.isLevelUnlocked(wordPairIndex, level)
  }

  const handleHelpClick = () => {
    toggleViewInstructions()
  }

  const switchToLevel = (newLevel) => {
    if (currentLevel === newLevel) return
    // Hide the "all levels solved" message when user switches levels
    if (allLevelsSolvedMessages.includes(completionMessage)) {
      setShowCompletionMessage(false)
    }

    if (gameContainerRef.current) {
      gameContainerRef.current.focus()
    }

    const index = wordPairIndex
    const start = getWordPair(newLevel, index)[0]
    const checkpoint = storage.getCheckpoint(index, newLevel)

    if (checkpoint) {
      setCurrentPath(checkpoint.currentPath || [])
      setCurrentWord(checkpoint.currentWord || start)
      setScore(checkpoint.score || 0)
      setProgressScore(checkpoint.score || 0)
      setProgressWord(checkpoint.currentWord || start)
      setHintedIndices(new Map(checkpoint.hintedIndices || []))
      setBoostedIndices(new Set(checkpoint.boostedIndices || []))
      if (checkpoint.hintIndex !== undefined && checkpoint.hintIndex >= 0) {
        setHintIndex(checkpoint.hintIndex)
        setCursorPosition(checkpoint.hintIndex)
        setProgressCursor(checkpoint.hintIndex)
      } else {
        setHintIndex(-1)
        setCursorPosition(-1)
        setProgressCursor(-1)
      }
      if (checkpoint.currentPath && checkpoint.currentPath.length > 0) {
        saveFirstTransformationFlag(newLevel, true)
      }
    } else {
      setCurrentPath([])
      setCurrentWord(start)
      setScore(0)
      setProgressScore(0)
      setProgressWord(start)
      setProgressCursor(-1)
      setCursorPosition(-1)
      setHintedIndices(new Map())
      setBoostedIndices(new Set())
      setHintIndex(-1)
      saveFirstTransformationFlag(newLevel, false)
    }

    setCurrentLevel(newLevel)
    storage.saveLastActiveLevel(wordPairIndex, newLevel)
    setBoostsRemaining(getBoostsRemainingFromStorage(index))
    setIsGameFinished(false)
    setShakeEffect(false)
    setHintExpanded(false)
    setBumpExpanded(false)
    setProgressPath([])
    setAchievedNewBest(false)
    setExpandedCircle('best')
    setWinEffect(false)
    setDanceEffect(false)
    setLetterDisappearCount(0)
    setAnimateScoreIn(false)
    setDeltaFlyState(null)
    document.body.style.backgroundColor = 'var(--uchu-yang)'
    const keyboardKeys = document.querySelectorAll('.keyboard-key')
    keyboardKeys.forEach((key) => {
      key.style.backgroundColor = `var(--uchu-light-gray-transparent)`
    })

    const parsedBest = storage.getBestScore(index, newLevel)
    const parsedPath = storage.getSavedPath(index, newLevel)

    if (parsedBest !== undefined && parsedBest >= getOptimalLength(newLevel) - 1) {
      setBest(parsedBest)
      setProgressPath(parsedPath)
      const startWord = getWordPair(newLevel, index)[0]
      const endWord = getWordPair(newLevel, index)[1]
      setModalWords([startWord, ...parsedPath, endWord])
      const savedHinted = storage.getSavedHintedIndices(index, newLevel)
      const savedBoosted = storage.getSavedBoostedIndices(index, newLevel)
      setBestHintedIndices(new Map(savedHinted))
      setBestBoostedIndices(new Set(savedBoosted))
    } else {
      setBest(999)
      setModalWords(getOptimalPath(newLevel, index))
      setBestHintedIndices(new Map())
      setBestBoostedIndices(new Set())
    }

    // If restoring a checkpoint with in-progress path, show score circle
    if (checkpoint && checkpoint.currentPath && checkpoint.currentPath.length > 0) {
      setExpandedCircle('score')
      setModalWords([start, ...checkpoint.currentPath])
    }

    prevTotalScoreRef.current = getTotalScore()
  }

  const handleRetryClick = () => {
    setCurrentPath([])
    setProgressWord('')
    setProgressCursor(-1)
    setProgressScore(0)
    // Reset first transformation flag for current level
    saveFirstTransformationFlag(currentLevel, false)
    // Clear checkpoint when retrying
    storage.clearCheckpoint(wordPairIndex, currentLevel)
    initGame(wordPairIndex, true)
  }

  const handleUnlockLevel = (level) => {
    storage.unlockLevel(wordPairIndex, level)
    setHighestLevel(level)
    switchToLevel(level)
  }

  const generateShareText = () => {
    const boldNum = (n) => String(n).split('').map(c => {
      const d = c.charCodeAt(0) - 48
      return d >= 0 && d <= 9 ? String.fromCodePoint(0x1D7EC + d) : c
    }).join('')
    let text = `𝗌𝗎𝖻𝗹𝗲𝘁𝘁𝗲𝗿𝘀 #${activePuzzleDisplayNumber}\n`
    text += `total score: ${boldNum(getTotalScore())}\n`

    for (let level = 1; level <= 4; level++) {
      if (isSolved(level).solved) {
        const [startWord, endWord] = getWordPair(level, todayIndex)
        const levelScore = getLevelScore(level)
        text += `${startWord} → ${endWord} : ${levelScore}\n`
      }
    }

    text = text.trim()
    return text
  }

  const handleShare = async () => {
    const isFirefox =
      typeof navigator.userAgent !== 'undefined' &&
      /firefox/i.test(navigator.userAgent)
    let text = generateShareText()

    if (navigator.share) {
      try {
        if (isFirefox) {
          // Firefox has issues with Web Share API
          try {
            await navigator.clipboard.writeText(text)
          } catch (error) {
            console.error('Copy to clipboard failed:', error)
          }
          return
        }
        await navigator.share({
          text: text,
        })
      } catch (error) {
        console.error('Sharing failed:', error)
      }
    } else {
      // Fallback to clipboard for browsers without Web Share API
      try {
        await navigator.clipboard.writeText(text)
      } catch (error) {
        // Safari desktop may need execCommand fallback
        try {
          const textArea = document.createElement('textarea')
          textArea.value = text
          textArea.style.position = 'fixed'
          textArea.style.left = '-9999px'
          textArea.style.top = '-9999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        } catch (execError) {
          console.error('Copy to clipboard failed:', execError)
        }
      }
    }
  }

  const handleCharacterClick = (index) => {
    setCursorPosition(index)
    setProgressCursor(index)
    // Hide the "first select a letter to change" message when a letter is selected
    // and mark as seen so it never shows again
    if (completionMessage === 'first select a letter to change') {
      setShowCompletionMessage(false)
      storage.markKeyboardHelpSeen()
    }
    // Hide the "all levels solved" message (sub-optimal) when user interacts with card
    // to encourage them to keep improving their solutions
    if (allLevelsSolvedMessages.includes(completionMessage)) {
      setShowCompletionMessage(false)
    }
    if (completionMessage.includes('not in word list')) {
      setShowCompletionMessage(false)
      setShakeEffect(false)
      if (errorMessageTimeoutRef.current) {
        clearTimeout(errorMessageTimeoutRef.current)
        errorMessageTimeoutRef.current = null
      }
    }
  }

  const handleKeyDown = (e) => {
    if (e.hasOwnProperty('preventDefault')) {
      e.preventDefault()
    }

    if (!isGameStarted || isGameFinished || cursorPosition < 0) {
      return
    }

    if ('vibrate' in navigator) {
      navigator.vibrate(1)
      setCanVibrate(true)
    }

    if (e.key === 'ArrowLeft') {
      setShakeEffect(false)
      setShowCompletionMessage(false)
      if (errorMessageTimeoutRef.current) {
        clearTimeout(errorMessageTimeoutRef.current)
        errorMessageTimeoutRef.current = null
      }
      let newCursorPosition = Math.max(0, cursorPosition - 1)
      setCursorPosition(newCursorPosition)
      setProgressCursor(newCursorPosition)
    } else if (e.key === 'ArrowRight') {
      setShakeEffect(false)
      setShowCompletionMessage(false)
      if (errorMessageTimeoutRef.current) {
        clearTimeout(errorMessageTimeoutRef.current)
        errorMessageTimeoutRef.current = null
      }
      let newCursorPosition = Math.min(
        currentWord.length - 1,
        cursorPosition + 1
      )
      setCursorPosition(newCursorPosition)
      setProgressCursor(newCursorPosition)
    } else if (e.key.length === 1 && e.key.match(/[a-z]/i)) {
      const newWord =
        currentWord.slice(0, cursorPosition) +
        e.key.toLowerCase() +
        currentWord.slice(cursorPosition + 1)
      if (newWord === currentWord) return
      if (isValidWord(newWord)) {
        // Track if this move was assisted by a hint and which letter
        const wasHinted = hintIndex !== -1
        const hintedLetterIdx = hintIndex // Capture before clearing

        // Clear any active hint underline after a valid move
        if (hintIndex !== -1) {
          setHintIndex(-1)
        }
        // Clear any pending error state from a previous invalid word
        setShakeEffect(false)
        setShowCompletionMessage(false)
        if (errorMessageTimeoutRef.current) {
          clearTimeout(errorMessageTimeoutRef.current)
          errorMessageTimeoutRef.current = null
        }
        setCurrentWord(newWord)
        setProgressWord(newWord)
        setScore((prevScore) => {
          const newScore = prevScore + 1
          setProgressScore(newScore)
          const end = getWordPair(currentLevel, wordPairIndex)[1]
          setCurrentPath((prevList) => {
            const updatedList = [...prevList, newWord]
            // Mark that first transformation has been made for this level
            saveFirstTransformationFlag(currentLevel, true)

            // Track if this word was hinted - store on the PREVIOUS word (where hint was shown)
            // pathIndex -1 means the start word
            let newHintedIndices = hintedIndices
            if (wasHinted) {
              newHintedIndices = new Map([...hintedIndices, [updatedList.length - 2, hintedLetterIdx]])
              setHintedIndices(newHintedIndices)
            }

            // Save checkpoint after every valid move (so progress persists on refresh/level switch)
            storage.saveCheckpoint(wordPairIndex, currentLevel, {
              currentWord: newWord,
              currentPath: updatedList,
              score: newScore,
              hintedIndices: [...newHintedIndices],
              boostedIndices: [...boostedIndices]
            })

            // Check if game is complete
            if (newWord === end) {
              setIsGameFinished(true)
              setProgressWord('')
              // Update modalWords with the complete path including final word
              const startWord = getWordPair(currentLevel, wordPairIndex)[0]
              setModalWords([startWord, ...updatedList])
              setExpandedCircle('score')
              // Clear checkpoint on completion
              storage.clearCheckpoint(wordPairIndex, currentLevel)

              // Trigger visual effects immediately to avoid two-frame jank
              const optimal = getOptimalLength(currentLevel)
              const isOptimalSolve = newScore === optimal

              if (best > 0 && newScore < best && !areTodaysSolutionsRevealed()) {
                setProgressPath([...updatedList.slice(0, -1)])
                setAchievedNewBest(true)
                setBest(newScore)
                setExpandedCircle('best') // Set immediately to prevent flash of collapsed state
                storage.saveScore(wordPairIndex, currentLevel, newScore, updatedList.slice(0, -1), [...newHintedIndices], [...boostedIndices])
                // Update best path indices for display
                setBestHintedIndices(new Map(newHintedIndices))
                setBestBoostedIndices(new Set(boostedIndices))

                // Only animate score container black if total score actually increased
                const newTotal = getTotalScore()
                if (newTotal > prevTotalScoreRef.current) {
                  const danceDelay = 1500
                  const letterDelay = 70
                  const wordLength = currentWord.length
                  // New timeline: dance -> letters disappear -> delta flies -> score animates
                  const allLettersGoneTime = danceDelay + (wordLength * letterDelay)
                  const scoreAnimateTime = allLettersGoneTime + 950 + FLY_DURATION

                  // Clear any existing black animation timeout
                  if (blackAnimationTimeoutRef.current) {
                    clearTimeout(blackAnimationTimeoutRef.current)
                  }
                  // Set up the black animation to trigger when score flies in
                  blackAnimationTimeoutRef.current = setTimeout(() => {
                    setScoreAnimationBlack(true)
                    // Reset after 1 second
                    blackAnimationTimeoutRef.current = setTimeout(() => {
                      setScoreAnimationBlack(false)
                      blackAnimationTimeoutRef.current = null
                    }, 1000)
                  }, scoreAnimateTime)
                }
              }

              if (isOptimalSolve) {
                const keyboardKeys = document.querySelectorAll('.keyboard-key')
                keyboardKeys.forEach((key) => {
                  key.style.backgroundColor = `var(--target-light-color)`
                })
                setWinEffect(true)
              }
              prevTotalScoreRef.current = getTotalScore()
              setDanceEffect(true)

              if (isGameStarted) {
                setProgressScore(0)
                setProgressCursor(-1)
              }
            } else {
              setExpandedCircle('score')
              setModalWords([getWordPair(currentLevel, wordPairIndex)[0], ...updatedList])
            }

            return updatedList
          })
          return newScore
        })
      } else {
        const errorMsg = `"${newWord}" not in word list`
        setCompletionMessage(errorMsg)
        completionMessageRef.current = errorMsg
        setShowCompletionMessage(true)
        if (errorMessageTimeoutRef.current) {
          clearTimeout(errorMessageTimeoutRef.current)
        }
        setShakeEffect(true)
        setShakeCount(c => c + 1)
        errorMessageTimeoutRef.current = setTimeout(() => {
          setShakeEffect(false)
          setShowCompletionMessage(false)
          errorMessageTimeoutRef.current = null
        }, 2000)
      }
    }
  }

  function Accordion() {
    const [activeSection, setActiveSection] = useState('')
    
    const instructionSections = [
      {
        id: 'scoring',
        title: 'SCORING',
        content: (
          <>
            <div style={{ margin: '0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                <thead>
                  <tr style={{  }}>
                    <th style={{ padding: '0.25em', textAlign: 'center', borderRight: '0px solid var(--uchu-gray)' }}></th>
                    <th colSpan="2" style={{ borderBottom: '1px solid var(--uchu-gray)', padding: '0.25em', textAlign: 'center' }}>Points Per Solution</th>
                  </tr>
                  <tr style={{ borderBottom: '2px solid var(--uchu-gray)' }}>
                    <th style={{ padding: '0.25em', textAlign: 'center', borderRight: '1px solid var(--uchu-gray)' }}>Level</th>
                    <th style={{ padding: '0.25em', textAlign: 'center', borderRight: '1px solid var(--uchu-gray)' }}>Optimal</th>
                    <th style={{ padding: '0.25em', textAlign: 'center' }}>Other</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid var(--uchu-gray)' }}>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>1</td>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>100</td>
                    <td style={{ padding: '0.25em' }}>50</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--uchu-gray)' }}>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>2</td>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>200</td>
                    <td style={{ padding: '0.25em' }}>150</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--uchu-gray)' }}>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>3</td>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>300</td>
                    <td style={{ padding: '0.25em' }}>250</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid var(--uchu-gray)' }}>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>4</td>
                    <td style={{ padding: '0.25em', borderRight: '1px solid var(--uchu-gray)' }}>400</td>
                    <td style={{ padding: '0.25em' }}>350</td>
                  </tr>
                  <tr style={{ borderBottom: '0px solid var(--uchu-gray)', fontWeight: 'bold' }}>
                    <td style={{ padding: '0.25em', borderBottom: '1px solid var(--uchu-gray)', borderRight: '1px solid var(--uchu-gray)' }}>Total</td>
                    <td style={{ padding: '0.25em', borderBottom: '1px solid var(--uchu-gray)', borderRight: '1px solid var(--uchu-gray)' }}>1000 (max score)</td>
                    <td style={{ padding: '0.25em' }}></td>
                  </tr>
                </tbody>
              </table>
              <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
                <li>Your running score is displayed in the upper right corner.</li>
                <li><strong style={{ color: 'var(--uchu-purple)' }}>WORD</strong>s cost 75 points each.</li>
                <li><strong style={{ color: 'var(--uchu-blue)' }}>LETTER</strong>s cost 30 points each.</li>
                <li><em><strong><u>Scores will not be saved when playing in private or incognito mode</u></strong></em>.</li>
              </ul>
            </div>
          </>
        )
      },
      {
        id: 'sharing',
        title: 'SHARING',
        content: (
          <>
            <div style={{ margin: '0' }}>
              <ul>
                <li>
                  <strong><em>Tap</em></strong> your score in the top right corner to share your progress with others.
                </li>
              </ul>
            </div>
          </>
        )
      },
    ]

    // Add iOS install section conditionally
    if (isIos && !isAndroid) {
      instructionSections.push({
        id: 'install',
        title: 'INSTALL',
        content: (
          <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
            <li>To install <strong>subletters</strong> as an app, <strong><em>tap</em></strong> the share icon on your browser.</li>
            <li>Then <strong><em>tap</em></strong> "Add to Home Screen".</li>
          </ul>
        )
      })
    }

    const handleReplayTutorial = () => {
      // Save current game state
      savedGameStateRef.current = {
        wordPairIndex,
        currentLevel,
        currentPath: [...currentPath],
        currentWord,
        score,
        best,
        highestLevel,
        boostsRemaining,
      }
      setViewInstructions(false)
      setIsReplayingTutorial(true)
      setShowTutorial(true)
      // Reset to tutorial state - level 1 only
      setHighestLevel(1)
      // Initialize with tutorial word pairs, reset to level 1
      initGame(TUTORIAL_WORD_INDEX, true, 1)
      prevTotalScoreRef.current = 0
    }

    const items = [
      {
        title: "PUZZLES",
        content: (
          <>
            <SolutionsViewer />
          </>
        ),
      },
      {
        title: 'RULES',
        content: (
          <>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleReplayTutorial}
                style={{
                  backgroundColor: 'var(--uchu-red)',
                  color: 'var(--uchu-yang)',
                  border: 'none',
                  borderRadius: '0.25em',
                  padding: '0.5em 1.25em',
                  fontSize: '1em',
                  fontWeight: 'bold',
                  fontFamily: 'Itim',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                PLAY TUTORIAL
              </button>
            </div>
            <div style={{ marginBottom: '0em' }}>
              {instructionSections.map((section) => (
                <div key={section.id} style={{
                  marginTop: '0.5em',
                  borderRadius: '0.25em',
                  border: '2px solid var(--uchu-gray)',
                  overflow: 'hidden'
                  }}>
                  <button
                    onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                    style={{
                      width: '100%',
                      padding: '0.25em 0.5em',
                      marginBottom: '0em',
                      backgroundColor: activeSection === section.id ? 'var(--uchu-gray)' : 'var(--uchu-gray)',
                      color: activeSection === section.id ? 'var(--uchu-yin)' : 'var(--uchu-yin)',
                      border: 'none',
                      borderRadius: '0',
                      fontSize: '1.1em',
                      fontWeight: 'bold',
                      fontFamily: 'Itim',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'background-color 0.15s ease, color 0.15s ease'
                    }}
                  >
                    {section.title}
                  </button>
                  {activeSection === section.id && (
                    <div style={{ 
                      padding: '0.5em 1em', 
                      backgroundColor: 'var(--uchu-yang)',
                      borderRadius: '0',
                      marginBottom: '0em'
                    }}>
                      {section.content}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ),
      },
    ]

    const handleTitleClick = (index) => {
      setActiveIndex((curr) => index)
    }

    return (
      <div className="callout">
        <div className="accordion-titles">
          {items.map((item, index) => (
            <span
              key={index}
              className={`accordion-title ${index === 0 ? 'rules' : 'solutions'} ${index === activeIndex ? 'active' : ''}`}
              onClick={() => handleTitleClick(index)}
            >
              {item.title}
            </span>
          ))}
        </div>

        <div className="accordion-content">
          {items.map((item, index) => (
            <div
              key={index}
              className={`content ${index === activeIndex ? 'open' : ''}`}
            >
              {index === activeIndex && item.content}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={gameContainerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        outline: 'none',
        transition: 'background-color 1s ease-in-out',
        backgroundColor: winEffect
          ? isGameStarted
            ? `var(--target-color)`
            : `var(--uchu-yang)`
          : `var(--uchu-yang)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 'calc(var(--vh, 1vh) * 100)',
      }}
    >
      {!loading && (
        <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '90%', maxWidth: '25em', margin: '0 auto' }}>
            <div style={{ flex: 1, paddingLeft: '0', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', alignSelf: 'center' }}>
              {/* Help Button moved to top-left - hidden during tutorial */}
              {!showTutorial && (
              <button
                onClick={handleHelpClick}
                style={{
                  backgroundColor: viewInstructions ? 'var(--uchu-red)' : 'transparent',
                  color: viewInstructions ? 'var(--uchu-yang)' : 'var(--uchu-red)',
                  height: '1.8em',
                  width: '1.8em',
                  borderRadius: '100%',
                  border: '0px solid var(--uchu-red)',
                  cursor: 'pointer',
                  fontSize: '1.5em',
                  fontWeight: 'bold',
                  padding: 0,
                  lineHeight: 1,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  boxSizing: 'content-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.3s ease, color 0.3s ease',
                }}
              >
{viewInstructions ? (
                    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  ) : (
                    <span style={{ marginTop: '-0.05em', marginLeft: '0.02em' }}>?</span>
                  )}
              </button>
              )}
            </div>
            <div style={{ flex: 1, position: 'relative', textAlign: 'center' }}>
              <h1
                style={{
                  transition: 'color 1s ease-in-out',
                  fontFamily: 'Title',
                  fontSize: '2.5em',
                  fontWeight: 'normal',
                  color: winEffect ? `var(--uchu-yin)` : `var(--uchu-yin)`,
                  margin: '0.15em 0 0.15em',
                }}
              >
                <span
                  style={{
                    transition: 'color 1s ease-in-out',
                    color: winEffect
                      ? isGameStarted
                        ? `var(--target-dark-color)`
                        : `var(--uchu-dark-gray)`
                      : 'var(--uchu-dark-gray)',
                  }}
                >
                  sub
                </span>
                letters
              </h1>
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', alignSelf: 'center', paddingRight: '0', position: 'relative' }}>
              {/* Score Box - tap to share */}
              <div
                ref={scoreContainerRef}
                data-score-display
                className={`score-container animating${animateScoreIn ? ' score-animate-in' : ''}`}
                onClick={areAnyLevelsSolved() ? handleShare : undefined}
                style={{
                  position: 'relative',
                  zIndex: 10,
                  fontSize: '1.5em',
                  fontWeight: 'bold',
                  color: scoreAnimationBlack ? 'var(--uchu-yin)' : scoreAnimationPurple ? 'var(--uchu-purple)' : scoreAnimation ? 'var(--uchu-blue)' : 'var(--uchu-yin)',
                  '--barbershop-gradient': scoreAnimationBlack
                    ? 'repeating-linear-gradient(45deg, var(--uchu-light-gray), var(--uchu-light-gray) 4px, rgba(255, 255, 255, 0.3) 4px, rgba(255, 255, 255, 0.3) 8px)'
                    : scoreAnimationPurple
                      ? 'repeating-linear-gradient(45deg, var(--uchu-light-purple), var(--uchu-light-purple) 4px, rgba(255, 255, 255, 0.3) 4px, rgba(255, 255, 255, 0.3) 8px)'
                      : scoreAnimation
                        ? 'repeating-linear-gradient(45deg, var(--uchu-light-blue), var(--uchu-light-blue) 4px, rgba(255, 255, 255, 0.3) 4px, rgba(255, 255, 255, 0.3) 8px)'
                        : 'repeating-linear-gradient(45deg, var(--uchu-light-gray), var(--uchu-light-gray) 4px, rgba(255, 255, 255, 0.3) 4px, rgba(255, 255, 255, 0.3) 8px)',
                  padding: '0.15em 0.5em',
                  borderRadius: '1em',
                  minWidth: '2.25em',
                  textAlign: 'center',
                  border: scoreAnimationBlack ? '2px solid var(--uchu-yin)' : scoreAnimationPurple ? '2px solid var(--uchu-purple)' : scoreAnimation ? '2px solid var(--uchu-blue)' : '2px solid var(--uchu-light-gray)',
                  transition: 'transform 0.3s ease, border 0.3s ease, color 0.3s ease',
                  transform: (scoreAnimation || scoreAnimationPurple || scoreAnimationBlack) ? 'scale(1.06)' : 'scale(1)',
                  cursor: areAnyLevelsSolved() ? 'pointer' : 'default',
                  display: viewInstructions ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: (areAnyLevelsSolved() || solutionsRevealedFlag) ? '0.15em' : '0',
                  marginLeft: '0.5em',
                }}
              >
                {solutionsRevealedFlag && (
                  // Lock icon when solutions revealed
                  <svg
                    width="0.75em"
                    height="0.75em"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ opacity: 1 }}
                  >
                    <rect x="5" y="12" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2.5" />
                    <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                )}
                {(danceEffect || letterDisappearCount > 0 || deltaFlyState) ? prevTotalScoreRef.current : getTotalScore()}
                {areAnyLevelsSolved() && (Object.values(solveStatuses).filter(s => s.solved).length > 1 || !(danceEffect || letterDisappearCount > 0 || deltaFlyState) || prevTotalScoreRef.current > 0) && (
                  // Share icon - iPhone style (arrow out of open tray)
                  <svg
                    width="0.85em"
                    height="0.85em"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 3v13" stroke="var(--uchu-yin)" strokeWidth="2" strokeLinecap="round" />
                    <path d="M7 8l5-5 5 5" stroke="var(--uchu-yin)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 11H7a2 2 0 0 0-2 2v6a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3v-6a2 2 0 0 0-2-2h-2" stroke="var(--uchu-yin)" strokeWidth="2" strokeLinecap="round" fill="none" />
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* Feature announcement banner */}
          {pendingAnnouncement && !showTutorial && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5em',
              margin: '0 0.75em 0.5em',
              padding: '0.6em 0.75em',
              borderRadius: '8px',
              backgroundColor: 'var(--uchu-light-red)',
              color: 'var(--uchu-red)',
              fontSize: '0.85em',
              fontWeight: 'bold',
            }}>
              <span style={{ flex: 1 }}>✦ New: {pendingAnnouncement.text}</span>
              <button
                onClick={() => {
                  storage.markAnnouncementSeen(pendingAnnouncement.id)
                  setPendingAnnouncement(null)
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--uchu-red)',
                  fontSize: '1.1em',
                  lineHeight: 1,
                  padding: '0 0.2em',
                  flexShrink: 0,
                }}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          {/* Instructions - shown when viewing instructions */}
          {viewInstructions && (
            <div
              style={{
                width: '90%',
                maxWidth: '25em',
                margin: '0 auto',
              }}
            >
              <Accordion />
            </div>
          )}

          {/* Game Controls Container - hidden when viewing instructions */}
          {!viewInstructions && (
          <div
            style={{
              width: '90%',
              maxWidth: '25em',
              margin: '0 auto',
            }}
          >
              <div
                style={{
                  animation: shakeEffect
                ? (shakeCount % 2 === 0 ? 'var(--shake-animation)' : 'var(--shake-alt-animation)')
                : danceEffect
                  ? 'var(--dance-animation)'
                  : 'none',
              pointerEvents: isGameStarted ? 'auto' : 'auto',
              position: 'relative',
              margin: '0em auto 1em',
              maxWidth: '25em',
              willChange: (danceEffect || shakeEffect) ? 'transform' : 'auto',
            }}
          >
            {/* card */}
            <div
              style={{
                backgroundColor: shakeEffect
                  ? 'var(--uchu-light-red)'
                  : isSolved(currentLevel).solved_optimally
                    ? `var(--solved-card-color)`
                    : `var(--level-${currentLevel}-color)`,
                borderRadius:
                  currentLevel === 1
                    ? '1em 1em 1em 0'
                    : currentLevel === 4
                      ? `1em 1em 0 1em`
                      : `1em 1em 1em 1em`,
                borderBottom: shakeEffect
                  ? `4px solid var(--uchu-red)`
                  : isSolved(currentLevel).solved_optimally
                    ? `4px solid var(--solved-card-border-color)`
                    : `4px solid var(--level-${currentLevel}-accent-color)`,
                borderLeft: shakeEffect
                  ? `4px solid var(--uchu-red)`
                  : isSolved(currentLevel).solved_optimally
                    ? `4px solid var(--solved-card-border-color)`
                    : `4px solid var(--level-${currentLevel}-accent-color)`,
                padding: '0.5em 2em',
                position: 'relative',
                zIndex: '1',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                  width: `auto`,
                  maxWidth: '100%',
                  height: `64px`,
                  color: `var(--level-${currentLevel}-text-color)`,
                }}
              >
                <div
                  data-start-word
                  style={{
                    paddingRight: '0.5em',
                    fontSize: '1.5em',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getWordPair(currentLevel, wordPairIndex)[0]}
                </div>
                {/* sourced and modified from https://github.com/theproductfactory/handy-arrows */}
                {/* license: https://creativecommons.org/licenses/by/4.0/ */}
                <svg
                  width="100%"
                  height="100%"
                  viewBox="-2 -2 211 68"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M184.005 0.213186C182.621 0.77654 181.659 2.20837 181.659 3.71065C181.659 5.58849 182.409 6.41007 186.211 8.66349C188.557 10.0484 192.945 13.3111 193.109 13.8041C193.25 14.2031 190.622 14.3205 187.619 14.0388C183.559 13.6867 181.377 13.2877 177.388 12.161C171.006 10.4005 165.82 9.57892 161.009 9.57892C152.539 9.57892 146.367 12.4661 142.472 18.2639C139.563 22.6299 137.005 25.3528 135.503 25.7519C134.236 26.104 131.632 25.7519 126.516 24.5782C115.23 21.9727 110.936 21.5032 106.055 22.3717C98.3114 23.7097 92.6095 27.5123 85.8282 35.8218C80.4547 42.4177 78.9295 43.1219 70.6699 42.8402C63.0673 42.582 57.5061 41.8779 47.4397 39.9765C40.2594 38.5916 35.9184 38.3569 31.6948 39.0611C26.9079 39.8592 20.6663 42.4177 15.7856 45.5631C9.70821 49.5066 3.32578 55.7035 0.650786 60.3277C-0.405132 62.1351 -0.147018 63.567 1.21394 63.567C1.7067 63.567 2.62183 62.7923 5.81305 59.6235C11.6793 53.8022 15.6917 50.7742 21.3468 47.9104C25.6408 45.7274 29.841 44.5069 34.0882 44.1782C36.6458 43.9905 39.6493 44.4364 47.0642 46.103C57.9285 48.5208 64.2874 49.3423 72.899 49.3423C78.8591 49.3658 79.9385 49.2015 82.7308 47.7931C85.7578 46.2673 87.6349 44.5773 90.7558 40.6338C91.7413 39.3662 93.7358 37.1598 95.1672 35.7279C98.1003 32.7938 100.588 31.1272 103.615 30.0474C108.378 28.3104 112.32 28.4982 122.339 30.916C131.89 33.2163 134.588 33.5214 137.568 32.5825C140.994 31.5262 142.895 29.6484 147.611 22.6534C150.849 17.8884 153.243 16.7382 159.836 16.7617C163.027 16.7852 163.919 16.8791 167.11 17.5363C169.105 17.9588 172.578 18.8273 174.807 19.4611C177.036 20.0949 179.781 20.7756 180.908 20.9634C183.864 21.4563 189.918 21.8319 192.077 21.6441C193.837 21.4798 193.931 21.5033 193.743 21.9258C193.626 22.1605 193.274 23.0994 192.969 24.0148C191.842 27.1602 192.265 34.6716 193.626 35.3993C194.189 35.7044 194.376 35.4697 195.573 33.052C196.324 31.5028 198.06 28.9442 201.275 24.6017C203.809 21.1746 206.085 17.9588 206.367 17.4424C206.672 16.8321 206.86 16.034 206.86 15.2125C206.883 12.8182 205.71 11.4098 203.012 10.6118C201.909 10.2831 201.486 9.95452 199.539 7.91236C194.752 2.86565 186.985 -0.960467 184.005 0.213186Z"
                    fill={`var(--level-${currentLevel}-text-color)`}
                    transform="rotate(10 0 0) translate(12.5 -13) scale(0.9 0.75)"
                  />
                </svg>
                <div
                  data-target-word
                  style={{
                    paddingLeft: '0.5em',
                    fontSize: '1.5em',
                    fontWeight: 'bold',
                  }}
                >
                  {getWordPair(currentLevel, wordPairIndex)[1]}
                </div>
              </div>
              <div ref={cardRef} style={{ position: 'relative' }}>
                <div className="card-text">
                  {currentWord.split('').map((char, index) => (
                    <span
                      key={index}
                      data-letter-index={index}
                      className={letterDisappearCount > index ? 'letter-disappear' : ''}
                      style={{
                        pointerEvents: isGameStarted ? 'auto' : 'none',
                        position: 'relative',
                        display: 'inline-block',
                        width: '1.25em',
                        color: (!isGameFinished && hintIndex === index)
                          ? 'var(--uchu-blue)'
                          : `var(--level-${currentLevel}-text-color)`,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        opacity: letterDisappearCount > index ? 0 : 1,
                        transform: letterDisappearCount > index ? 'scale(0.5) translateY(-20px)' : 'none',
                        transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
                        transitionDelay: letterDisappearCount > 0 ? `${index * 70}ms` : '0ms',
                      }}
                      onClick={() => handleCharacterClick(index)}
                    >
                      {char}
                      {!isGameFinished && index === cursorPosition && (
                        <>
                          {/* Blinking cursor */}
                          <span
                            style={{
                              position: 'absolute',
                              bottom: '-0.125em',
                              left: '0',
                              width: '100%',
                              height: '4px',
                              backgroundColor: (!isGameFinished && hintIndex === index)
                                ? 'var(--uchu-blue)'
                                : `var(--level-${currentLevel}-text-color)`,
                              borderRadius: '1em',
                              animation: 'blink 1s infinite',
                            }}
                          ></span>
                          {/* Tap animation */}
                          {!isGameStarted && isMobile && (
                            <span
                              className="tap"
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '0.25em',
                                height: '0.25em',
                                borderRadius: '50%',
                                visibility: 'hidden',
                                backgroundColor: `var(--uchu-yin)`,
                                animation: 'tap 1.5s ease-out',
                              }}
                            ></span>
                          )}
                        </>
                      )}
                    </span>
                  ))}
                </div>
              </div>
              <ScoreCircles
                score={score}
                best={best}
                words={modalWords}
                currentLevel={currentLevel}
                handleWordClick={handleWordClick}
                expandedCircle={expandedCircle}
                handleScoreCircleClick={handleScoreCircleClick}
                handleBestCircleClick={handleBestCircleClick}
                hintedIndices={hintedIndices}
                boostedIndices={boostedIndices}
                bestHintedIndices={bestHintedIndices}
                bestBoostedIndices={bestBoostedIndices}
                hintIndex={hintIndex}
                optimalLength={getOptimalLength(currentLevel)}
                hasFirstTransformation={loadFirstTransformationFlag(wordPairIndex, currentLevel)}
                achievedNewBest={achievedNewBest}
                isCurrentLevelSolvedOptimally={isSolved(currentLevel).solved_optimally}
              />
            </div>
            {/* tab container */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-evenly', // Ensures equal spacing
                alignItems: 'center',
                height: '4em',
                width: 'calc(100%)', // Make the tab container take full width of the card
                position: 'absolute',
                bottom: '-3.5em',
                borderRadius: '0 0 1em 1em',
                margin: '0em auto 1em',
                gap: `0.25em`,
              }}
            >
              {/* tabs */}
              {[...Array(4)].map((_, i) => {
                const levelNum = i + 1

                const isNewlyAvailable =
                  levelNum !== 1 &&
                  isGameStarted &&
                  isSolved(levelNum - 1).solved &&
                  !hasUnlockedLevel(levelNum) &&
                  highestLevel < 4 &&
                  !showTutorial

                const isUnlocked = showTutorial
                  ? levelNum <= highestLevel
                  : isGameStarted
                    ? levelNum <= highestLevel || hasUnlockedLevel(levelNum)
                    : levelNum === 1 || isSolved(levelNum - 1).solved

                const isVisible = isUnlocked

                const isSelected = levelNum === currentLevel
                return (
                  <div
                    key={i}
                    ref={isNewlyAvailable ? levelRef : null}
                    data-level-tab={levelNum}
                    onClick={() => {
                      if (isNewlyAvailable) {
                        handleUnlockLevel(levelNum)
                      } else if (isUnlocked) {
                        switchToLevel(levelNum)
                      }
                    }}
                    style={{
                      flex: '1',
                      maxWidth: '25%',
                      height: isSelected ? '1.5em' : '2.5em',
                      transform: isSelected
                        ? 'translate(0em, 1px)'
                        : 'translate(0em, -0.75em)',
                      borderRadius:
                        levelNum === 4 && !isSelected
                          ? '0.5em 0.5em 0.5em 0.5em'
                          : '0 0 0.5em 0.5em',
                      borderBottom: isSolved(levelNum).solved_optimally
                        ? isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--solved-card-border-color)`
                          : `4px solid var(--solved-card-border-dark-color)`
                        : isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--level-${levelNum}-accent-color)`
                          : isNewlyAvailable
                            ? `4px solid var(--uchu-gray-5)`
                            : `4px solid var(--uchu-gray-5)`,
                      borderLeft: isSolved(levelNum).solved_optimally
                        ? isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--solved-card-border-color)`
                          : `4px solid var(--solved-card-border-dark-color)`
                        : isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--level-${levelNum}-accent-color)`
                          : isNewlyAvailable
                            ? `4px solid var(--uchu-gray-5)`
                            : `4px solid var(--uchu-gray-5)`,
                      cursor:
                        isNewlyAvailable || isVisible ? 'pointer' : 'default',

                      visibility: isVisible ? 'visible' : 'hidden',

                      animation: isNewlyAvailable
                        ? 'slide-down 0.6s ease forwards'
                        : 'none',
                      animationDelay: isNewlyAvailable && danceEffect
                        ? `${1500 + (currentWord.length * 70) + 950 + FLY_DURATION + 300}ms`
                        : '0s',
                      opacity: isNewlyAvailable ? 1 : undefined,
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                      fontSize: '1em',
                      fontWeight: 'bold',
                      backgroundColor: isVisible
                        ? isSelected
                          ? shakeEffect
                            ? `var(--uchu-light-red)`
                            : isSolved(levelNum).solved_optimally
                              ? `var(--solved-card-color)`
                              : `var(--level-${levelNum}-color)`
                          : isSolved(levelNum).solved_optimally
                            ? `var(--solved-card-dark-color)`
                            : `var(--uchu-gray-2)`
                        : isNewlyAvailable
                          ? `var(--uchu-gray-2)`
                          : 'transparent',
                      zIndex: isSelected ? '2' : '0',
                      WebkitTapHighlightColor: 'transparent',
                      outline: 'none',
                      touchAction: 'manipulation',
                    }}
                  ></div>
                )
              })}
            </div>
          </div>
          
            <div
              className={`completion-message ${
                showCompletionMessage ? 'show' : 'hide'
              }`}
              style={{
                color: (completionMessage === 'first select a letter to change' || completionMessage.includes('not in word list'))
                  ? 'var(--uchu-red)'
                  : undefined
              }}
            >
              {(() => {
                const isError = completionMessage === 'first select a letter to change' || completionMessage.includes('not in word list');

                if (isError) {
                  return completionMessage;
                } else {
                  return (
                    <span key={completionMessage} className="congrats-text">
                      {completionMessage}
                    </span>
                  );
                }
              })()}
            </div>

          {isGameStarted && (
            <QwertyKeyboard
              handleKeyDown={handleKeyDown}
              handleShare={handleShare}
              handleHelpClick={handleHelpClick}
              cursorPosition={cursorPosition}
              onKeyboardTapWithoutSelection={showInstructionMessage}
            />
          )}

            {/* Buttons row below keyboard: Hint, Boost */}
            <div className="keyboard-row" style={{ width: '100%', maxWidth: '25em', margin: '0 auto', display: 'flex' }}>
                {/* X button for LETTER */}
                <button
                  onClick={cancelHint}
                  style={{
                    flex: hintExpanded ? '0 0 2.5em' : '0 0 0',
                    minWidth: 0,
                    width: hintExpanded ? '2.5em' : 0,
                    whiteSpace: 'nowrap',
                    height: '2.5em',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--uchu-red)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                    padding: hintExpanded ? '0 0.6em' : 0,
                    marginRight: hintExpanded ? '0.5em' : 0,
                    fontSize: '0.95em',
                    fontWeight: 'bold',
                    color: 'white',
                    overflow: 'hidden',
                    opacity: hintExpanded ? 1 : 0,
                  }}
                >
                  <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                {/* LETTER button - always present, transforms when expanded */}
                <button
                  data-hint-button
                  onClick={hintExpanded ? undefined : handleHint}
                  disabled={!hintExpanded && (isGameFinished || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed() || hintIndex !== -1)}
                  style={{
                    flex: bumpExpanded ? '0 0 0' : 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    height: '2.5em',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--uchu-light-blue)',
                    cursor: hintExpanded ? 'default' : (isGameFinished || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed() || hintIndex !== -1) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                    opacity: bumpExpanded ? 0 : hintExpanded ? 1 : (isGameFinished || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed() || hintIndex !== -1) ? 0.5 : 1,
                    padding: bumpExpanded ? 0 : '0 1em',
                    marginRight: (!hintExpanded && !bumpExpanded) ? '0.5em' : 0,
                    fontSize: '0.95em',
                    fontWeight: 'bold',
                    color: 'var(--uchu-dark-blue)',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                  }}
                >
                  {hintExpanded ? 'reveal letter? −30 pts' : 'LETTER'}
                </button>
                {/* Checkmark button for LETTER */}
                <button
                  data-confirm-hint
                  onClick={confirmHint}
                  style={{
                    flex: hintExpanded ? '0 0 2.5em' : '0 0 0',
                    minWidth: 0,
                    width: hintExpanded ? '2.5em' : 0,
                    whiteSpace: 'nowrap',
                    height: '2.5em',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--uchu-green)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                    padding: hintExpanded ? '0 0.6em' : 0,
                    marginLeft: hintExpanded ? '0.5em' : 0,
                    fontSize: '0.95em',
                    fontWeight: 'bold',
                    color: 'white',
                    overflow: 'hidden',
                    opacity: hintExpanded ? 1 : 0,
                  }}
                >
                  <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </button>
                {/* X button for WORD */}
                <button
                  onClick={cancelBoost}
                  style={{
                    flex: bumpExpanded ? '0 0 2.5em' : '0 0 0',
                    minWidth: 0,
                    width: bumpExpanded ? '2.5em' : 0,
                    whiteSpace: 'nowrap',
                    height: '2.5em',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--uchu-red)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                    padding: bumpExpanded ? '0 0.6em' : 0,
                    marginRight: bumpExpanded ? '0.5em' : 0,
                    fontSize: '0.95em',
                    fontWeight: 'bold',
                    color: 'white',
                    overflow: 'hidden',
                    opacity: bumpExpanded ? 1 : 0,
                  }}
                >
                  <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                {/* WORD button - always present, transforms when expanded */}
                <button
                  data-bump-button
                  onClick={bumpExpanded ? undefined : handleBoost}
                  disabled={!bumpExpanded && (boostsRemaining === 0 || isGameFinished || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed())}
                  style={{
                    flex: hintExpanded ? '0 0 0' : 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    height: '2.5em',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--uchu-light-purple)',
                    cursor: bumpExpanded ? 'default' : (boostsRemaining === 0 || isGameFinished || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed()) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                    opacity: hintExpanded ? 0 : bumpExpanded ? 1 : (boostsRemaining === 0 || isGameFinished || isSolved(currentLevel).solved_optimally || areTodaysSolutionsRevealed()) ? 0.5 : 1,
                    padding: hintExpanded ? 0 : '0 1em',
                    fontSize: '0.95em',
                    fontWeight: 'bold',
                    color: 'var(--uchu-dark-purple)',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                  }}
                >
                  {bumpExpanded ? (
                    'reveal word? −75 pts'
                  ) : (
                    <>
                      WORD{' '}
                      {[0, 1, 2, 3].map((i) => (
                        <span
                          key={i}
                          style={{
                            width: '0.45em',
                            height: '0.45em',
                            borderRadius: '50%',
                            backgroundColor: i < boostsRemaining ? 'var(--uchu-dark-purple)' : 'var(--uchu-purple-2)',
                            marginLeft: i === 0 ? '0.5em' : '0.15em',
                            display: 'inline-block',
                          }}
                        />
                      ))}
                    </>
                  )}
                </button>
                {/* Checkmark button for WORD */}
                <button
                  data-confirm-boost
                  onClick={confirmBoost}
                  style={{
                    flex: bumpExpanded ? '0 0 2.5em' : '0 0 0',
                    minWidth: 0,
                    width: bumpExpanded ? '2.5em' : 0,
                    whiteSpace: 'nowrap',
                    height: '2.5em',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--uchu-green)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'flex 0.3s ease, width 0.3s ease, opacity 0.3s ease, padding 0.3s ease, margin 0.3s ease',
                    padding: bumpExpanded ? '0 0.6em' : 0,
                    marginLeft: bumpExpanded ? '0.5em' : 0,
                    fontSize: '0.95em',
                    fontWeight: 'bold',
                    color: 'white',
                    overflow: 'hidden',
                    opacity: bumpExpanded ? 1 : 0,
                  }}
                >
                  <svg width="1.3em" height="1.3em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </button>
            </div>

            <div
              style={{
                display: 'flex',
                marginBottom: 'auto',
                alignItems: 'center',
                position: isGameStarted ? 'inherit' : 'relative',
                marginTop: isGameStarted ? '0' : '-1em',
              }}
            ></div>
          </div>
          )}
        </div>
      )}
      <footer
        style={{
          transition: 'background-color 1s ease-in-out, color 1s ease-in-out',
          backgroundColor: winEffect
            ? isGameStarted
              ? `var(--target-color)`
              : `var(--uchu-yang)`
            : 'var(--uchu-yang)',
          color: winEffect
            ? isGameStarted
              ? `var(--target-dark-color)`
              : `var(--uchu-gray)`
            : 'var(--uchu-gray)',
        }}
      >
        <p style={{}}>
          <span style={{ fontFamily: 'Title', fontSize: '1.4em' }}>
            subletters
          </span>
          <sup style={{ fontSize: '0.8em' }}>™</sup>
          {!showTutorial && (
            <>
              {' '}
              <span style={{ fontFamily: 'Title', fontSize: '1.25em', color: 'var(--uchu-dark-gray)' }}>#{activePuzzleDisplayNumber}</span>
            </>
          )}
        </p>
        <p style={{}}>&copy; 2025 Alexander Bolinsky and Peter Branden</p>
        <p style={{}}>
          <a
            style={{
              transition: 'background-color 1s ease-in-out, color 1s ease-in-out',
              color: winEffect
                ? isGameStarted
                  ? `var(--target-dark-color)`
                  : `var(--uchu-gray)`
                : `var(--uchu-gray)`,
            }}
            href="/privacy.html"
          >
            Privacy Policy
          </a>
        </p>
      </footer>
      {/* Flying delta animation */}
      {deltaFlyState && (
        <div
          className="score-delta-flying"
          style={{
            top: deltaFlyState.flying ? deltaFlyState.targetTop : deltaFlyState.sourceTop,
            left: deltaFlyState.flying ? deltaFlyState.targetLeft : deltaFlyState.sourceLeft,
            fontSize: deltaFlyState.flying ? '1.5em' : '2.5em',
            opacity: deltaFlyState.flying ? 0 : 1,
            color: `var(--level-${currentLevel}-text-color)`,
            animation: 'score-pop-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          +{scoreDelta}
        </div>
      )}
      {showTutorial && (
        <TutorialOverlay
          onComplete={() => {
            setShowTutorial(false)
            storage.markTutorialCompleted()

            if (isReplayingTutorial && savedGameStateRef.current) {
              // Restore saved game state
              const saved = savedGameStateRef.current
              setIsReplayingTutorial(false)
              savedGameStateRef.current = null
              // Clear tutorial puzzle data only
              storage.clearPuzzleData(TUTORIAL_WORD_INDEX)
              // Reinitialize with saved state
              initGame(saved.wordPairIndex, false, saved.currentLevel)
              setHighestLevel(saved.highestLevel)
            } else {
              // First time tutorial - clear and start fresh
              storage.clearPuzzleData(TUTORIAL_WORD_INDEX)
              storage.clearPuzzleData(todayIndex)
              initGame(todayIndex, true, 1)
              prevTotalScoreRef.current = 0
              setScore(0)
              setBoostsRemaining(4)
              setCurrentLevel(1)
              setHighestLevel(1)
            }
          }}
          startStep={isReplayingTutorial ? 1 : 0}
          currentWord={currentWord}
          startWord={getWordPair(currentLevel, TUTORIAL_WORD_INDEX)[0]}
          targetWord={getWordPair(currentLevel, TUTORIAL_WORD_INDEX)[1]}
          cursorPosition={cursorPosition}
          currentPath={currentPath}
          optimalPath={getOptimalPath(currentLevel, TUTORIAL_WORD_INDEX)}
          currentLevel={currentLevel}
          isGameFinished={isGameFinished}
          score={score}
          hintIndex={hintIndex}
          hintExpanded={hintExpanded}
          bumpExpanded={bumpExpanded}
          onChangeLevel={(level) => switchToLevel(level)}
          modalWords={modalWords}
        />
      )}
    </div>
  )
}

export default Game
